import { KreatorPaymentsSection } from './KreatorPaymentsSection';
import { useCallback, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import {
  Image as ImageIcon,
  Loader2,
  Plus,
  ChevronDown,
  GripVertical,
  RefreshCw,
  Settings2,
  ShoppingBag,
  Truck,
  X,
  MapPinned,
  Percent,
  Trash2,
  CreditCard,
  ExternalLink,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { pickupMapsApi } from '../../lib/api';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { kreatorApi, type WcCountry, type WcGeneralSetting, type WcGeneralSettingValue, type WcPaymentGateway, type WcShippingAssetsConfig, type WcShippingConfig, type WcShippingCostRule, type WcShippingMethodSetting, type WcShippingRulesConfig, type WcShippingZone, type WcShippingZoneLocation, type WcShippingZoneMethod, type WcTaxClass, type WcTaxConfig, type WcTaxRate, type WpSiteOptions } from '../../lib/api';
import { createTenantKreatorAdapter, type KreatorApiAdapter } from '../../lib/kreator-api-adapter';
import type { KreatorSettingsData } from '../KreatorPage';
import { SettingsSaveBar } from '../../components/SettingsSaveBar';
import { WpMediaPickerModal } from '../../components/WpMediaPickerModal';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { Select } from '../../components/ui/Select';
import { SettingsSection } from '../../components/ui/SettingsSection';
import { Textarea } from '../../components/ui/Textarea';
import { ToggleRow } from '../../components/ui/ToggleRow';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';

interface Props {
  integrationId: string;
  storeId?: string | null;
  wordpressUrl?: string;
  settings: KreatorSettingsData | null;
  onSaved: () => void;
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  apiAdapter?: KreatorApiAdapter;
}

type SettingValue = string | number | boolean | null;

type MethodDraft = {
  enabled: boolean;
  order: number;
  logoUrl: string;
  logoMaxWidth: string;
  dispatchEstimate?: string;
  deliveryKind?: 'address' | 'pickup';
  pickupProvider?: 'plugin' | 'inpost-geowidget';
  pickupPointTypes?: 'lockers' | 'points' | 'all';
  pickupStorage?: 'native' | 'furgonetka';
  settings: Record<string, SettingValue>;
};

type ZoneDraft = {
  name: string;
  order: number;
  locations: WcShippingZoneLocation[];
};

type MethodLogoPickerTarget = {
  zoneId: number;
  instanceId: number;
};

const METHOD_COST_RULE_PREFIX = 'method_cost_';

type ShippingMethodOption = {
  id: string;
  label: string;
};

type ShopTab = 'general' | 'taxes' | 'shipping' | 'payments';

type WcGeneralForm = Record<string, WcGeneralSettingValue>;
type TaxRateDraft = Omit<WcTaxRate, 'priority'> & { priority: number | '' };

type WpSiteOptionsForm = {
  siteTitle: string;
  tagline: string;
  siteIconId: number | null;
  siteIconUrl: string;
  timezoneString: string;
  permalinkStructure: string;
};

const WOO_GENERAL_SETTING_IDS = [
  'woocommerce_store_address',
  'woocommerce_store_address_2',
  'woocommerce_store_city',
  'woocommerce_default_country',
  'woocommerce_store_postcode',
  'woocommerce_allowed_countries',
  'woocommerce_specific_allowed_countries',
  'woocommerce_all_except_countries',
  'woocommerce_ship_to_countries',
  'woocommerce_specific_ship_to_countries',
  'woocommerce_default_customer_address',
  'woocommerce_enable_coupons',
  'woocommerce_calc_discounts_sequentially',
  'woocommerce_enable_address_autocomplete',
  'woocommerce_currency',
  'woocommerce_currency_pos',
  'woocommerce_price_thousand_sep',
  'woocommerce_price_decimal_sep',
  'woocommerce_price_num_decimals',
] as const;

const TAX_ENABLE_SETTING_ID = 'woocommerce_calc_taxes';

const WOO_TAX_SETTING_IDS = [
  TAX_ENABLE_SETTING_ID,
  'woocommerce_prices_include_tax',
  'woocommerce_tax_based_on',
  'woocommerce_shipping_tax_class',
  'woocommerce_tax_round_at_subtotal',
  'woocommerce_tax_classes',
  'woocommerce_tax_display_shop',
  'woocommerce_tax_display_cart',
  'woocommerce_price_display_suffix',
  'woocommerce_tax_total_display',
] as const;

const WOO_SHIPPING_SETTING_IDS = [
  'woocommerce_enable_shipping_calc',
  'woocommerce_shipping_cost_requires_address',
  'woocommerce_shipping_hide_rates_when_free',
  'woocommerce_ship_to_destination',
] as const;

const SHIPPING_LOGO_WIDTH_MIN = 40;
const SHIPPING_LOGO_WIDTH_MAX = 400;
const SHIPPING_LOGO_WIDTH_DEFAULT = 200;

const EMPTY_SHIPPING_RULES: WcShippingRulesConfig = {
  costRules: [],
  paymentRules: [],
  hiddenPaymentGatewayIds: [],
  paymentGateways: [],
};

const TAX_ENABLE_FALLBACK_SETTING: WcGeneralSetting = {
  id: TAX_ENABLE_SETTING_ID,
  label: 'Włącz podatki',
  description: 'Włącz stawki podatkowe i obliczenia podatku w WooCommerce.',
  type: 'checkbox',
  value: 'no',
  default: 'no',
};

const TAX_DETAIL_FALLBACK_SETTINGS: WcGeneralSetting[] = [
  {
    id: 'woocommerce_prices_include_tax',
    label: 'Ceny wpisywane z podatkiem',
    description: 'Określa, czy ceny produktów w WooCommerce są wpisywane jako brutto czy netto.',
    type: 'select',
    value: 'no',
    default: 'no',
    options: {
      yes: 'Tak, będę wpisywać ceny zawierające podatek',
      no: 'Nie, będę wpisywać ceny bez podatku',
    },
  },
  {
    id: 'woocommerce_tax_based_on',
    label: 'Obliczaj podatek w oparciu o',
    description: 'Adres używany przez WooCommerce do obliczania podatku.',
    type: 'select',
    value: 'shipping',
    default: 'shipping',
    options: {
      shipping: 'Adres wysyłki klienta',
      billing: 'Adres rozliczeniowy klienta',
      base: 'Adres bazowy sklepu',
    },
  },
  {
    id: 'woocommerce_shipping_tax_class',
    label: 'Klasa podatkowa wysyłki',
    description: 'Klasa podatku stosowana do kosztu dostawy.',
    type: 'select',
    value: 'inherit',
    default: 'inherit',
    options: {
      inherit: 'Klasa podatkowa oparta o produkty w koszyku',
      '': 'Standardowa stawka',
      'obnizona-stawka': 'Obniżona stawka',
      'zerowa-stawka': 'Zerowa stawka',
    },
  },
  {
    id: 'woocommerce_tax_round_at_subtotal',
    label: 'Zaokrąglanie',
    description: 'Zaokrąglaj podatek na sumie częściowej zamiast per wiersz.',
    type: 'checkbox',
    value: 'no',
    default: 'no',
  },
  {
    id: 'woocommerce_tax_classes',
    label: 'Dodatkowe klasy podatkowe',
    description: 'Każda klasa w osobnej linii. EcomFlow tworzy dla nich zakładki stawek.',
    type: 'textarea',
    value: 'Obniżona stawka\nZerowa stawka',
    default: 'Obniżona stawka\nZerowa stawka',
  },
  {
    id: 'woocommerce_tax_display_shop',
    label: 'Wyświetlanie cen w sklepie',
    description: 'Sposób prezentacji cen na stronach sklepu.',
    type: 'select',
    value: 'excl',
    default: 'excl',
    options: {
      incl: 'Z podatkiem',
      excl: 'Bez podatku',
    },
  },
  {
    id: 'woocommerce_tax_display_cart',
    label: 'Wyświetlanie cen w koszyku i zamówieniu',
    description: 'Sposób prezentacji cen w koszyku i na kasie.',
    type: 'select',
    value: 'excl',
    default: 'excl',
    options: {
      incl: 'Z podatkiem',
      excl: 'Bez podatku',
    },
  },
  {
    id: 'woocommerce_price_display_suffix',
    label: 'Sufiks wyświetlanej ceny',
    description: 'Tekst dopisywany przy cenie, np. "z VAT".',
    type: 'text',
    value: '',
    default: '',
  },
  {
    id: 'woocommerce_tax_total_display',
    label: 'Wyświetlanie sumy podatków',
    description: 'Sposób agregowania podatków w podsumowaniu zamówienia.',
    type: 'select',
    value: 'itemized',
    default: 'itemized',
    options: {
      itemized: 'Dla każdego przedmiotu',
      single: 'Jako jedna suma',
    },
  },
];

const SHIPPING_FALLBACK_SETTINGS: WcGeneralSetting[] = [
  {
    id: 'woocommerce_enable_shipping_calc',
    label: 'Kalkulator wysyłki w koszyku',
    description: 'Pozwala klientowi wyliczyć koszty wysyłki przed przejściem do kasy.',
    type: 'checkbox',
    value: 'no',
    default: 'no',
  },
  {
    id: 'woocommerce_shipping_cost_requires_address',
    label: 'Ukryj koszty przed wpisaniem adresu',
    description: 'Koszty dostawy pojawią się dopiero po podaniu danych potrzebnych do wyliczenia wysyłki.',
    type: 'checkbox',
    value: 'no',
    default: 'no',
  },
  {
    id: 'woocommerce_shipping_hide_rates_when_free',
    label: 'Ukryj płatne metody przy darmowej wysyłce',
    description: 'Gdy dostępna jest darmowa wysyłka, pozostałe stawki zostaną ukryte.',
    type: 'checkbox',
    value: 'no',
    default: 'no',
  },
  {
    id: 'woocommerce_ship_to_destination',
    label: 'Domyślny adres wysyłki',
    description: 'Określa, który adres WooCommerce traktuje jako domyślny przy wysyłce.',
    type: 'select',
    value: 'billing',
    default: 'billing',
    options: {
      shipping: 'Domyślnie adres wysyłki',
      billing: 'Domyślnie adres płatności',
      billing_only: 'Wymuszaj wysyłkę na adres płatności',
    },
  },
];

const PERMALINK_OPTIONS = [
  { value: '', label: 'Prosty' },
  { value: '/%postname%/', label: 'Nazwa wpisu' },
  { value: '/%year%/%monthnum%/%day%/%postname%/', label: 'Dzień i nazwa' },
  { value: '/%year%/%monthnum%/%postname%/', label: 'Miesiąc i nazwa' },
  { value: '/archives/%post_id%', label: 'Numeryczny' },
] as const;

function buildWpSiteOptionsForm(options?: WpSiteOptions | null): WpSiteOptionsForm {
  return {
    siteTitle: options?.siteTitle ?? '',
    tagline: options?.tagline ?? '',
    siteIconId: options?.siteIconId ?? null,
    siteIconUrl: options?.siteIconUrl ?? '',
    timezoneString: options?.timezoneString ?? 'Europe/Warsaw',
    permalinkStructure: options?.permalinkStructure ?? '/%postname%/',
  };
}

function tabButtonClass(active: boolean): string {
  return [
    'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap',
    active
      ? 'border border-accent-primary/30 bg-accent-primary/15 text-accent-primary'
      : 'border border-transparent text-text-secondary hover:bg-surface-hover/60 hover:text-text-primary',
  ].join(' ');
}

type CleanShippingFieldConfig = {
  label: string;
  helper?: string;
};

const CORE_SHIPPING_METHOD_IDS = new Set(['flat_rate', 'free_shipping', 'local_pickup']);

const SHIPPING_SETTING_ORDER = ['title', 'tax_status', 'cost', 'requires', 'min_amount', 'ignore_discounts'];

const CLEAN_SHIPPING_FIELDS: Record<string, CleanShippingFieldConfig> = {
  title: {
    label: 'Nazwa metody',
    helper: 'Nazwa widoczna dla klienta w koszyku i kasie.',
  },
  tax_status: {
    label: 'Status podatku',
    helper: 'Określa, czy koszt tej dostawy ma być opodatkowany.',
  },
  cost: {
    label: 'Koszt dostawy netto',
    helper: 'Kwota netto zapisywana technicznie w WooCommerce.',
  },
  requires: {
    label: 'Warunek darmowej dostawy',
    helper: 'Reguła, która musi być spełniona, aby metoda była dostępna.',
  },
  min_amount: {
    label: 'Minimalna wartość zamówienia netto',
    helper: 'Próg koszyka wymagany do darmowej dostawy, liczony od wartości netto zamówienia.',
  },
  ignore_discounts: {
    label: 'Sprawdzaj próg przed rabatem',
    helper: 'Minimalna wartość zamówienia jest liczona przed naliczeniem kuponów.',
  },
};

const WOO_OPTION_TRANSLATIONS: Record<string, string> = {
  'Free shipping requires...': 'Darmowa dostawa wymaga...',
  'N/A': 'Nie dotyczy',
  'A valid free shipping coupon': 'Ważnego kuponu na darmową dostawę',
  'A minimum order amount': 'Minimalnej wartości zamówienia',
  'A minimum order amount OR a coupon': 'Minimalnej wartości zamówienia albo kuponu',
  'A minimum order amount AND a coupon': 'Minimalnej wartości zamówienia i kuponu',
  'Taxable': 'Opodatkowana',
  'None': 'Brak',
};

function translateWooOption(value: string | number | null | undefined): string {
  const text = decodeHtmlEntities(value).trim();
  return WOO_OPTION_TRANSLATIONS[text] ?? text;
}

function isCoreShippingMethod(methodId: string): boolean {
  return CORE_SHIPPING_METHOD_IDS.has(methodId);
}

function shouldShowShippingCostField(method: WcShippingZoneMethod): boolean {
  if (method.method_id === 'free_shipping') return false;
  return true;
}

function hasNativeShippingCostSetting(method: WcShippingZoneMethod): boolean {
  return Boolean(method.settings?.cost);
}

function cleanShippingSettings(method: WcShippingZoneMethod): Array<[string, WcShippingMethodSetting, CleanShippingFieldConfig]> {
  return SHIPPING_SETTING_ORDER
    .filter(settingKey => settingKey !== 'cost' && method.settings?.[settingKey] && CLEAN_SHIPPING_FIELDS[settingKey])
    .map(settingKey => [settingKey, method.settings[settingKey], CLEAN_SHIPPING_FIELDS[settingKey]]);
}

function settingValues(settings: Record<string, WcShippingMethodSetting>): Record<string, SettingValue> {
  const values: Record<string, SettingValue> = {};

  for (const key of SHIPPING_SETTING_ORDER) {
    const value = settings[key]?.value;
    if (value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
      values[key] = value;
    }
  }

  return values;
}

function shippingSettingsPayload(
  method: WcShippingZoneMethod,
  draft: MethodDraft,
): Record<string, SettingValue> {
  return Object.fromEntries(
    SHIPPING_SETTING_ORDER
      .filter(key => method.settings?.[key] && Object.prototype.hasOwnProperty.call(draft.settings, key))
      .map(key => [key, draft.settings[key]]),
  );
}

function hasInvalidFlexibleShippingRules(method: WcShippingZoneMethod): boolean {
  if (method.method_id !== 'flexible_shipping_single') return false;

  const value = method.settings?.method_rules?.value;
  let rules: unknown = value;

  if (typeof value === 'string') {
    try {
      rules = JSON.parse(value);
    } catch {
      return true;
    }
  }

  return !Array.isArray(rules)
    || rules.length === 0
    || rules.some(rule => {
      if (!rule || typeof rule !== 'object') return true;
      return !Array.isArray((rule as { conditions?: unknown }).conditions);
    });
}

function methodDraftKey(zoneId: number, instanceId: number): string {
  return `${zoneId}:${instanceId}`;
}

function shippingMethodAssetKey(method: Pick<WcShippingZoneMethod, 'method_id' | 'instance_id'>): string {
  return `${method.method_id}:${method.instance_id}`;
}

function shippingRateId(method: Pick<WcShippingZoneMethod, 'method_id' | 'instance_id'>): string {
  return `${method.method_id}:${method.instance_id}`;
}

function methodCostRuleId(method: Pick<WcShippingZoneMethod, 'instance_id'>): string {
  return `${METHOD_COST_RULE_PREFIX}${method.instance_id}`;
}

function isMethodCostRule(rule: WcShippingCostRule): boolean {
  return rule.id.startsWith(METHOD_COST_RULE_PREFIX);
}

function shippingMethodOptionLabel(zone: WcShippingZone, method: WcShippingZoneMethod): string {
  const methodTitle = method.title || method.method_title || method.method_id;
  return `${methodTitle} (${zone.name})`;
}

function shippingZoneDragKey(zoneId: number): string {
  return `zone:${zoneId}`;
}

function normalizeShippingLogoMaxWidth(value: unknown): number {
  if (value === '') return SHIPPING_LOGO_WIDTH_DEFAULT;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return SHIPPING_LOGO_WIDTH_DEFAULT;
  return Math.min(SHIPPING_LOGO_WIDTH_MAX, Math.max(SHIPPING_LOGO_WIDTH_MIN, Math.round(numeric)));
}

function shippingLogoMaxWidthInputValue(value: unknown): string {
  if (value === '') return '';
  return String(normalizeShippingLogoMaxWidth(value));
}

function normalizeTaxPriority(value: unknown): number {
  if (value === '') return 1;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(1, Math.round(numeric));
}

function buildZoneDrafts(zones: WcShippingZone[]): Record<number, ZoneDraft> {
  return Object.fromEntries(zones.map(zone => [
    zone.id,
    {
      name: zone.name,
      order: zone.order ?? 0,
      locations: zone.locations ?? [],
    },
  ]));
}

function buildMethodDrafts(zones: WcShippingZone[], assets: WcShippingAssetsConfig['assets'] = {}): Record<string, MethodDraft> {
  const drafts: Record<string, MethodDraft> = {};
  zones.forEach(zone => {
    zone.methods.forEach(method => {
      const assetKey = shippingMethodAssetKey(method);
      drafts[methodDraftKey(zone.id, method.instance_id)] = {
        enabled: method.enabled,
        order: method.order ?? 0,
        dispatchEstimate: assets[assetKey]?.dispatchEstimate ?? '',
        deliveryKind: assets[assetKey]?.deliveryKind ?? 'address',
        pickupProvider: assets[assetKey]?.pickupProvider ?? 'plugin',
        pickupPointTypes: assets[assetKey]?.pickupPointTypes ?? 'all',
        pickupStorage: assets[assetKey]?.pickupStorage ?? 'native',
        logoUrl: assets[assetKey]?.logoUrl ?? '',
        logoMaxWidth: shippingLogoMaxWidthInputValue(assets[assetKey]?.logoMaxWidth),
        settings: settingValues(method.settings ?? {}),
      };
    });
  });
  return drafts;
}

function createShippingCostRule(): WcShippingCostRule {
  return {
    id: `rule_${Date.now()}`,
    enabled: true,
    shippingMethodId: '',
    minSubtotal: '',
    maxSubtotal: '',
    cost: '0',
  };
}

function normalizeShippingRulesForUi(config?: WcShippingRulesConfig | null): WcShippingRulesConfig {
  return {
    costRules: (config?.costRules ?? []).map(rule => ({
      id: rule.id || `rule_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      enabled: rule.enabled !== false,
      shippingMethodId: rule.shippingMethodId ?? '',
      minSubtotal: rule.minSubtotal ?? '',
      maxSubtotal: rule.maxSubtotal ?? '',
      cost: rule.cost ?? '0',
    })),
    paymentRules: (config?.paymentRules ?? []).map(rule => ({
      paymentGatewayId: rule.paymentGatewayId,
      shippingMethodIds: rule.shippingMethodIds ?? [],
    })),
    hiddenPaymentGatewayIds: Array.from(new Set(config?.hiddenPaymentGatewayIds ?? [])),
    paymentGateways: config?.paymentGateways ?? [],
  };
}

function shippingRulesPayload(config: WcShippingRulesConfig): WcShippingRulesConfig {
  return {
    costRules: config.costRules
      .filter(rule => rule.enabled || rule.minSubtotal || rule.maxSubtotal || rule.cost)
      .map(rule => ({
        id: rule.id,
        enabled: rule.enabled,
        shippingMethodId: rule.shippingMethodId ?? '',
        minSubtotal: rule.minSubtotal ?? '',
        maxSubtotal: rule.maxSubtotal ?? '',
        cost: rule.cost === '' ? '0' : rule.cost,
      })),
    paymentRules: config.paymentRules
      .map(rule => ({
        paymentGatewayId: rule.paymentGatewayId,
        shippingMethodIds: Array.from(new Set(rule.shippingMethodIds.filter(Boolean))),
      }))
      .filter(rule => rule.paymentGatewayId && rule.shippingMethodIds.length > 0),
    hiddenPaymentGatewayIds: Array.from(new Set((config.hiddenPaymentGatewayIds ?? []).filter(Boolean))),
  };
}

function countryCodesFromLocations(locations: WcShippingZoneLocation[]): string[] {
  return locations
    .filter(location => location.type === 'country')
    .map(location => location.code.toUpperCase());
}

function replaceCountryLocations(locations: WcShippingZoneLocation[], countryCodes: string[]): WcShippingZoneLocation[] {
  const otherLocations = locations.filter(location => location.type !== 'country');
  const countryLocations = countryCodes.map(code => ({ type: 'country', code: code.toUpperCase() }));
  return [...countryLocations, ...otherLocations];
}

function countryLabel(countries: WcCountry[], code: string): string {
  const country = countries.find(item => item.code.toUpperCase() === code.toUpperCase());
  return country ? `${country.name} (${country.code})` : code.toUpperCase();
}

function nonCountryLocationsLabel(locations: WcShippingZoneLocation[]): string {
  return locations
    .filter(location => location.type !== 'country')
    .map(location => `${location.type}:${location.code}`)
    .join(', ');
}

interface CountrySelectionFieldProps {
  id: string;
  value: WcShippingZoneLocation[];
  countries: WcCountry[];
  disabled?: boolean;
  onChange: (locations: WcShippingZoneLocation[]) => void;
}

function CountrySelectionField({ id, value, countries, disabled, onChange }: CountrySelectionFieldProps) {
  const selectedCountryCodes = countryCodesFromLocations(value);
  const selectedSet = new Set(selectedCountryCodes);
  const availableCountries = countries.filter(country => !selectedSet.has(country.code.toUpperCase()));
  const otherLocations = nonCountryLocationsLabel(value);

  const addCountry = (code: string) => {
    if (!code || selectedSet.has(code.toUpperCase())) return;
    onChange(replaceCountryLocations(value, [...selectedCountryCodes, code]));
  };

  const removeCountry = (code: string) => {
    onChange(replaceCountryLocations(value, selectedCountryCodes.filter(item => item !== code)));
  };

  return (
    <div className="space-y-3">
      <Select
        id={id}
        value=""
        disabled={disabled || countries.length === 0}
        placeholder={countries.length === 0 ? 'Ładowanie krajów...' : 'Dodaj kraj'}
        onChange={(event) => addCountry(event.target.value)}
      >
        <option value="">Dodaj kraj</option>
        {availableCountries.map(country => (
          <option key={country.code} value={country.code}>{country.name} ({country.code})</option>
        ))}
      </Select>

      {selectedCountryCodes.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selectedCountryCodes.map(code => (
            <span
              key={code}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border-subtle bg-surface-raised/60 px-2.5 py-1 text-xs text-text-primary"
            >
              <span className="truncate">{countryLabel(countries, code)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="rounded-full p-0.5 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
                  onClick={() => removeCountry(code)}
                  aria-label={`Usuń kraj ${countryLabel(countries, code)}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-text-muted">Brak wybranych krajów.</p>
      )}

      {otherLocations && (
        <p className="text-xs text-warning">
          Zachowano lokalizacje spoza listy państw: {otherLocations}
        </p>
      )}
    </div>
  );
}

function buildWcGeneralForm(settings: WcGeneralSetting[]): WcGeneralForm {
  return buildWcSettingsForm(settings, WOO_GENERAL_SETTING_IDS);
}

function buildWcSettingsForm(settings: WcGeneralSetting[], settingIds: readonly string[]): WcGeneralForm {
  return Object.fromEntries(
    settings
      .filter(setting => settingIds.includes(setting.id))
      .map(setting => [setting.id, setting.value ?? '']),
  );
}

function buildWcTaxForm(config: WcTaxConfig): WcGeneralForm {
  return buildWcSettingsForm(
    [
      config.enableTaxes ?? TAX_ENABLE_FALLBACK_SETTING,
      ...mergeTaxDetailSettings(config.settings),
    ],
    WOO_TAX_SETTING_IDS,
  );
}

function mergeSettingsWithFallbacks(settings: WcGeneralSetting[], fallbackSettings: WcGeneralSetting[], settingIds: readonly string[]): WcGeneralSetting[] {
  const byId = new Map(fallbackSettings.map(setting => [setting.id, setting]));
  settings
    .filter(setting => settingIds.includes(setting.id))
    .forEach(setting => {
      const fallback = byId.get(setting.id);
      byId.set(setting.id, {
        ...(fallback ?? setting),
        ...setting,
        label: fallback?.label || setting.label || setting.id,
        description: fallback?.description || setting.description || setting.tip || '',
        options: setting.options && Object.keys(setting.options).length > 0 ? setting.options : fallback?.options,
      });
    });

  return fallbackSettings.map(setting => byId.get(setting.id) ?? setting);
}

function mergeTaxDetailSettings(settings: WcGeneralSetting[]): WcGeneralSetting[] {
  return mergeSettingsWithFallbacks(
    settings.filter(setting => setting.id !== TAX_ENABLE_SETTING_ID),
    TAX_DETAIL_FALLBACK_SETTINGS,
    WOO_TAX_SETTING_IDS,
  );
}

function mergeShippingSettings(settings: WcGeneralSetting[]): WcGeneralSetting[] {
  return mergeSettingsWithFallbacks(settings, SHIPPING_FALLBACK_SETTINGS, WOO_SHIPPING_SETTING_IDS);
}

function withWcSettingDefaults(form: WcGeneralForm, settings: WcGeneralSetting[]): WcGeneralForm {
  const next = { ...form };
  settings.forEach(setting => {
    if (next[setting.id] === undefined || next[setting.id] === null) {
      next[setting.id] = setting.value ?? setting.default ?? '';
    }
  });
  return next;
}

function normalizeTaxConfigForUi(config: WcTaxConfig): WcTaxConfig {
  const enableValue = String(config.enableTaxes?.value ?? TAX_ENABLE_FALLBACK_SETTING.value);
  if (enableValue !== 'yes') {
    return config;
  }
  return {
    ...config,
    settings: mergeTaxDetailSettings(config.settings),
  };
}

function kreatorErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const payload = data as { error?: unknown; message?: unknown };
    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
  }
  return fallback;
}

function isMissingWooShippingMethod(error: unknown): boolean {
  return kreatorErrorMessage(error, '').includes('woocommerce_rest_shipping_zone_method_invalid');
}

function isTransientWordPressError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 504 || status === 408) return true;

  const message = kreatorErrorMessage(error, (error as { message?: string })?.message ?? '');
  return /nie odpowiedział|limit czasu|timeout|ECONNABORTED|ETIMEDOUT/i.test(message);
}

function findWcSetting(settings: WcGeneralSetting[], id: string): WcGeneralSetting | undefined {
  return settings.find(setting => setting.id === id);
}

function wcFormString(form: WcGeneralForm, id: string): string {
  const value = form[id];
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function wcFormArray(form: WcGeneralForm, id: string): string[] {
  const value = form[id];
  return Array.isArray(value) ? value.map(item => item.toUpperCase()) : [];
}

function updateWcFormValue(setForm: Dispatch<SetStateAction<WcGeneralForm>>, id: string, value: WcGeneralSettingValue) {
  setForm(prev => ({ ...prev, [id]: value }));
}

function decodeHtmlEntities(text: string | number | null | undefined): string {
  return String(text ?? '')
    .replace(/&#x([0-9a-f]+);/gi, (_match, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&#(\d+);/g, (_match, code) => String.fromCodePoint(parseInt(code, 10)))
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function translateWcLabel(text: string | number | null | undefined): string {
  const value = decodeHtmlEntities(text).trim();
  const translations: Record<string, string> = {
    'Sell to all countries': 'Sprzedawaj do wszystkich krajów',
    'Sell to all countries, except for...': 'Sprzedawaj do wszystkich krajów oprócz...',
    'Sell to specific countries': 'Sprzedawaj do wybranych krajów',
    'Ship to all countries you sell to': 'Wysyłaj do wszystkich krajów, do których sprzedajesz',
    'Ship to all countries': 'Wysyłaj do wszystkich krajów',
    'Ship to specific countries only': 'Wysyłaj tylko do wybranych krajów',
    'Disable shipping & shipping calculations': 'Wyłącz wysyłkę i kalkulacje dostawy',
    'No location by default': 'Brak domyślnej lokalizacji',
    'Shop base address': 'Kraj/region sklepu',
    Geolocate: 'Geolokalizacja',
    'Geolocate (with page caching support)': 'Geolokalizacja ze wsparciem cache',
    'Left': 'Po lewej',
    'Right': 'Po prawej',
    'Left with space': 'Po lewej z odstępem',
    'Right with space': 'Po prawej z odstępem',
    'Default to customer shipping address': 'Domyślnie adres wysyłki',
    'Default to customer billing address': 'Domyślnie adres płatności',
    'Force shipping to the customer billing address': 'Wymuszaj wysyłkę na adres płatności',
  };
  return translations[value] ?? value;
}

interface WcCountryCodeSelectionFieldProps {
  id: string;
  value: string[];
  countries: WcCountry[];
  disabled?: boolean;
  onChange: (countryCodes: string[]) => void;
}

function WcCountryCodeSelectionField({ id, value, countries, disabled, onChange }: WcCountryCodeSelectionFieldProps) {
  const selectedCountryCodes = value.map(code => code.toUpperCase());
  const selectedSet = new Set(selectedCountryCodes);
  const availableCountries = countries.filter(country => !selectedSet.has(country.code.toUpperCase()));

  const addCountry = (code: string) => {
    const normalized = code.toUpperCase();
    if (!normalized || selectedSet.has(normalized)) return;
    onChange([...selectedCountryCodes, normalized]);
  };

  return (
    <div className="space-y-3">
      <Select
        id={id}
        value=""
        disabled={disabled || countries.length === 0}
        placeholder={countries.length === 0 ? 'Ładowanie krajów...' : 'Dodaj kraj'}
        onChange={(event) => addCountry(event.target.value)}
      >
        <option value="">Dodaj kraj</option>
        {availableCountries.map(country => (
          <option key={country.code} value={country.code}>{country.name} ({country.code})</option>
        ))}
      </Select>

      {selectedCountryCodes.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selectedCountryCodes.map(code => (
            <span
              key={code}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border-subtle bg-surface-raised/60 px-2.5 py-1 text-xs text-text-primary"
            >
              <span className="truncate">{countryLabel(countries, code)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="rounded-full p-0.5 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary"
                  onClick={() => onChange(selectedCountryCodes.filter(item => item !== code))}
                  aria-label={`Usuń kraj ${countryLabel(countries, code)}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-text-muted">Brak wybranych krajów.</p>
      )}
    </div>
  );
}

function WcTextField({
  setting,
  form,
  setForm,
  fallbackLabel,
  labelOverride,
  placeholder,
  type = 'text',
}: {
  setting?: WcGeneralSetting;
  form: WcGeneralForm;
  setForm: Dispatch<SetStateAction<WcGeneralForm>>;
  fallbackLabel: string;
  labelOverride?: string;
  placeholder?: string;
  type?: 'text' | 'number';
}) {
  if (!setting) return null;
  return (
    <FormField
      label={labelOverride ?? decodeHtmlEntities(setting.label || fallbackLabel)}
      htmlFor={`wc-general-${setting.id}`}
      helper={decodeHtmlEntities(setting.description || setting.tip)}
    >
      <Input
        id={`wc-general-${setting.id}`}
        type={type}
        value={wcFormString(form, setting.id)}
        placeholder={placeholder ?? decodeHtmlEntities(setting.placeholder)}
        onChange={(event) => updateWcFormValue(setForm, setting.id, event.target.value)}
      />
    </FormField>
  );
}

function WcSelectField({
  setting,
  form,
  setForm,
  fallbackLabel,
  labelOverride,
  fallbackOptions,
}: {
  setting?: WcGeneralSetting;
  form: WcGeneralForm;
  setForm: Dispatch<SetStateAction<WcGeneralForm>>;
  fallbackLabel: string;
  labelOverride?: string;
  fallbackOptions?: Array<{ value: string; label: string }>;
}) {
  if (!setting) return null;
  const options = setting.options && Object.keys(setting.options).length > 0
    ? Object.entries(setting.options).map(([value, label]) => ({ value, label: translateWcLabel(label) }))
    : fallbackOptions ?? [];
  return (
    <FormField
      label={labelOverride ?? decodeHtmlEntities(setting.label || fallbackLabel)}
      htmlFor={`wc-general-${setting.id}`}
      helper={decodeHtmlEntities(setting.description || setting.tip)}
    >
      <Select
        id={`wc-general-${setting.id}`}
        value={wcFormString(form, setting.id)}
        onChange={(event) => updateWcFormValue(setForm, setting.id, event.target.value)}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Select>
    </FormField>
  );
}

function WcToggleField({
  setting,
  form,
  setForm,
  fallbackLabel,
  labelOverride,
}: {
  setting?: WcGeneralSetting;
  form: WcGeneralForm;
  setForm: Dispatch<SetStateAction<WcGeneralForm>>;
  fallbackLabel: string;
  labelOverride?: string;
}) {
  if (!setting) return null;
  const value = wcFormString(form, setting.id);
  return (
    <ToggleRow
      checked={value === 'yes' || value === '1' || value === 'true'}
      onChange={(checked) => updateWcFormValue(setForm, setting.id, checked ? 'yes' : 'no')}
      label={labelOverride ?? decodeHtmlEntities(setting.label || fallbackLabel)}
      description={decodeHtmlEntities(setting.description || setting.tip)}
      className="rounded-card border border-border-subtle bg-surface-base/40 border-t"
    />
  );
}

function WcTextareaField({
  setting,
  form,
  setForm,
  fallbackLabel,
  labelOverride,
  minRows = 4,
}: {
  setting?: WcGeneralSetting;
  form: WcGeneralForm;
  setForm: Dispatch<SetStateAction<WcGeneralForm>>;
  fallbackLabel: string;
  labelOverride?: string;
  minRows?: number;
}) {
  if (!setting) return null;
  return (
    <FormField
      label={labelOverride ?? decodeHtmlEntities(setting.label || fallbackLabel)}
      htmlFor={`wc-general-${setting.id}`}
      helper={decodeHtmlEntities(setting.description || setting.tip)}
    >
      <Textarea
        id={`wc-general-${setting.id}`}
        minRows={minRows}
        value={wcFormString(form, setting.id)}
        placeholder={decodeHtmlEntities(setting.placeholder)}
        onChange={(event) => updateWcFormValue(setForm, setting.id, event.target.value)}
      />
    </FormField>
  );
}

function taxClassLabel(taxClass: WcTaxClass): string {
  const knownLabels: Record<string, string> = {
    standard: 'Standardowe stawki',
    'obnizona-stawka': 'Obniżona stawka',
    'reduced-rate': 'Obniżona stawka',
    'zerowa-stawka': 'Zerowa stawka',
    'zero-rate': 'Zerowa stawka',
  };
  return knownLabels[taxClass.slug] ?? taxClass.name ?? taxClass.slug;
}

function makeBlankTaxRate(taxClass: string): TaxRateDraft {
  return {
    country: '',
    state: '',
    postcode: '',
    city: '',
    rate: '',
    name: '',
    priority: 1,
    compound: false,
    shipping: true,
    class: taxClass,
  };
}

function normalizeTaxRate(rate: WcTaxRate, taxClass: string): TaxRateDraft {
  return {
    id: rate.id,
    country: rate.country ?? '',
    state: rate.state ?? '',
    postcode: rate.postcode ?? '',
    city: rate.city ?? '',
    rate: rate.rate ?? '',
    name: rate.name ?? '',
    priority: Number(rate.priority ?? 1),
    compound: Boolean(rate.compound),
    shipping: rate.shipping !== false,
    order: rate.order,
    class: rate.class ?? taxClass,
  };
}

function normalizeTaxRatesByClass(config: WcTaxConfig): Record<string, TaxRateDraft[]> {
  return Object.fromEntries(
    config.classes.map(taxClass => [
      taxClass.slug,
      (config.ratesByClass[taxClass.slug] ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(rate => normalizeTaxRate(rate, taxClass.slug)),
    ]),
  );
}

function sortTaxClasses(classes: WcTaxClass[]): WcTaxClass[] {
  const preferred = ['standard', 'obnizona-stawka', 'reduced-rate', 'zerowa-stawka', 'zero-rate'];
  return [...classes].sort((a, b) => {
    const aIndex = preferred.indexOf(a.slug);
    const bIndex = preferred.indexOf(b.slug);
    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    }
    return taxClassLabel(a).localeCompare(taxClassLabel(b), 'pl');
  });
}

interface TaxRateTableProps {
  taxClass: WcTaxClass;
  rates: TaxRateDraft[];
  onChange: (rates: TaxRateDraft[]) => void;
}

function TaxRateTable({ taxClass, rates, onChange }: TaxRateTableProps) {
  const updateRate = (index: number, patch: Partial<TaxRateDraft>) => {
    onChange(rates.map((rate, rateIndex) => (
      rateIndex === index ? { ...rate, ...patch, class: taxClass.slug } : rate
    )));
  };

  const removeRate = (index: number) => {
    onChange(rates.filter((_rate, rateIndex) => rateIndex !== index));
  };

  const addRate = () => {
    onChange([...rates, makeBlankTaxRate(taxClass.slug)]);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">{taxClassLabel(taxClass)}</h3>
          <p className="mt-0.5 text-xs text-text-muted">Stawki są zapisywane w tabeli podatków WooCommerce dla klasy {taxClass.slug}.</p>
        </div>
        <Button variant="secondary" size="sm" onClick={addRate} leftIcon={<Plus />}>
          Dodaj stawkę
        </Button>
      </div>

      {rates.length === 0 ? (
        <div className="rounded-card border border-dashed border-border-subtle px-4 py-8 text-center">
          <p className="text-sm text-text-muted">Brak stawek podatkowych w tej klasie.</p>
          <Button className="mt-4" variant="secondary" size="sm" onClick={addRate} leftIcon={<Plus />}>
            Dodaj pierwszą stawkę
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-card border border-border-subtle">
          <div className="min-w-[1120px]">
            <div className="grid grid-cols-[0.7fr_0.8fr_1fr_1fr_0.8fr_1.1fr_0.7fr_0.75fr_0.65fr_44px] gap-2 border-b border-border-subtle bg-surface-raised/60 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              <div>Kod kraju</div>
              <div>Kod woj.</div>
              <div>Kod pocztowy</div>
              <div>Miejscowość</div>
              <div>Stawka %</div>
              <div>Nazwa</div>
              <div>Priorytet</div>
              <div>Składana</div>
              <div>Wysyłka</div>
              <div />
            </div>
            <div className="divide-y divide-border-subtle">
              {rates.map((rate, index) => (
                <div
                  key={rate.id ? `tax-rate-${rate.id}` : `tax-rate-new-${index}`}
                  className="grid grid-cols-[0.7fr_0.8fr_1fr_1fr_0.8fr_1.1fr_0.7fr_0.75fr_0.65fr_44px] gap-2 px-3 py-3"
                >
                  <Input
                    value={rate.country}
                    placeholder="PL"
                    onChange={(event) => updateRate(index, { country: event.target.value.toUpperCase() })}
                  />
                  <Input
                    value={rate.state}
                    placeholder="np. MA"
                    onChange={(event) => updateRate(index, { state: event.target.value.toUpperCase() })}
                  />
                  <Input
                    value={rate.postcode}
                    placeholder="np. 00-*"
                    onChange={(event) => updateRate(index, { postcode: event.target.value })}
                  />
                  <Input
                    value={rate.city}
                    placeholder="np. Warszawa"
                    onChange={(event) => updateRate(index, { city: event.target.value })}
                  />
                  <Input
                    value={rate.rate}
                    placeholder="23.0000"
                    onChange={(event) => updateRate(index, { rate: event.target.value })}
                  />
                  <Input
                    value={rate.name}
                    placeholder="VAT"
                    onChange={(event) => updateRate(index, { name: event.target.value })}
                  />
                  <Input
                    type="number"
                    min={1}
                    value={rate.priority}
                    onChange={(event) => updateRate(index, {
                      priority: event.target.value === '' ? '' : normalizeTaxPriority(event.target.value),
                    })}
                  />
                  <label className="flex h-10 items-center justify-center rounded-input border border-border bg-surface-base/60">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border text-accent-primary focus:ring-accent-primary"
                      checked={rate.compound}
                      onChange={(event) => updateRate(index, { compound: event.target.checked })}
                      aria-label="Stawka składana"
                    />
                  </label>
                  <label className="flex h-10 items-center justify-center rounded-input border border-border bg-surface-base/60">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border text-accent-primary focus:ring-accent-primary"
                      checked={rate.shipping}
                      onChange={(event) => updateRate(index, { shipping: event.target.checked })}
                      aria-label="Wysyłka"
                    />
                  </label>
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => removeRate(index)}
                    leftIcon={<Trash2 />}
                    aria-label="Usuń stawkę podatku"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SortableShippingZoneProps {
  id: string;
  title: string;
  subtitle: string;
  disabled?: boolean;
  badges: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}

function SortableShippingZone({ id, title, subtitle, disabled, badges, actions, children }: SortableShippingZoneProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <section
      ref={setNodeRef}
      style={style}
      className={`overflow-hidden rounded-card border border-border-subtle bg-surface-base/40 ${isDragging ? 'opacity-70 shadow-lg' : ''}`}
    >
      <div className="border-b border-border-subtle bg-surface-raised/40 px-4 py-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <button
              ref={setActivatorNodeRef}
              type="button"
              disabled={disabled}
              className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-surface-base/60 text-text-muted transition-colors hover:border-border-strong hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Przeciągnij strefę"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate text-base font-semibold text-text-primary">{title}</h3>
                {badges}
              </div>
              <p className="mt-1 text-xs text-text-muted">{subtitle}</p>
            </div>
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      </div>

      {children}
    </section>
  );
}

interface SortableShippingMethodProps {
  id: string;
  title: string;
  meta: string;
  enabled: boolean;
  expanded: boolean;
  external?: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function SortableShippingMethod({
  id,
  title,
  meta,
  enabled,
  expanded,
  external,
  onToggle,
  children,
}: SortableShippingMethodProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`overflow-hidden rounded-card border border-border-subtle bg-surface-raised/45 ${isDragging ? 'opacity-70 shadow-lg' : ''}`}
    >
      <div className="flex w-full items-center gap-3 px-4 py-4 transition-colors hover:bg-surface-hover/35">
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-surface-base/60 text-text-muted transition-colors hover:border-border-strong hover:text-text-primary"
          aria-label="Przeciągnij metodę dostawy"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-semibold text-text-primary">{title}</span>
              <Badge variant={enabled ? 'success' : 'danger'} size="sm">
                {enabled ? 'Włączona' : 'Wyłączona'}
              </Badge>
              {external && (
                <Badge variant="neutral" size="sm">
                  Zewnętrzna
                </Badge>
              )}
            </div>
            <div className="mt-0.5 text-xs text-text-muted">{meta}</div>
          </div>
          <ChevronDown className={`h-4 w-4 shrink-0 text-text-secondary transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="space-y-4 border-t border-border-subtle p-4">
          {children}
        </div>
      )}
    </div>
  );
}

function renderSettingInput({
  settingKey,
  setting,
  config,
  value,
  onChange,
}: {
  settingKey: string;
  setting: WcShippingMethodSetting;
  config: CleanShippingFieldConfig;
  value: SettingValue;
  onChange: (value: SettingValue) => void;
}) {
  const type = setting.type || 'text';

  if (type === 'checkbox') {
    const checked = value === true || value === 'yes' || value === '1';
    return (
      <ToggleRow
        key={settingKey}
        checked={checked}
        onChange={(next) => onChange(next ? 'yes' : 'no')}
        label={config.label}
        description={config.helper}
        className="rounded-card border border-border-subtle bg-surface-base/40 border-t"
      />
    );
  }

  if (setting.options && Object.keys(setting.options).length > 0) {
    return (
      <FormField
        key={settingKey}
        label={config.label}
        htmlFor={`shipping-setting-${settingKey}`}
        helper={config.helper}
      >
        <Select
          id={`shipping-setting-${settingKey}`}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
        >
          {Object.entries(setting.options).map(([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>{translateWooOption(optionLabel)}</option>
          ))}
        </Select>
      </FormField>
    );
  }

  if (type === 'textarea') {
    return (
      <FormField
        key={settingKey}
        label={config.label}
        htmlFor={`shipping-setting-${settingKey}`}
        helper={config.helper}
      >
        <Textarea
          id={`shipping-setting-${settingKey}`}
          minRows={4}
          value={String(value ?? '')}
          placeholder={translateWooOption(setting.placeholder)}
          onChange={(event) => onChange(event.target.value)}
        />
      </FormField>
    );
  }

  const inputType = ['number', 'decimal', 'price'].includes(type) ? 'text' : 'text';
  return (
    <FormField
      key={settingKey}
      label={config.label}
      htmlFor={`shipping-setting-${settingKey}`}
      helper={config.helper}
    >
      <Input
        id={`shipping-setting-${settingKey}`}
        type={inputType}
        value={String(value ?? '')}
        placeholder={translateWooOption(setting.placeholder)}
        onChange={(event) => onChange(event.target.value)}
      />
    </FormField>
  );
}

export function KreatorShopSection({ integrationId, storeId, wordpressUrl, showSuccess, showError, apiAdapter }: Props) {
  const adapter = apiAdapter ?? createTenantKreatorAdapter(integrationId);
  const confirmDialog = useConfirmDialog();
  const [activeTab, setActiveTab] = useState<ShopTab>('general');
  const [generalSettings, setGeneralSettings] = useState<WcGeneralSetting[]>([]);
  const [generalForm, setGeneralForm] = useState<WcGeneralForm>({});
  const [wpSiteForm, setWpSiteForm] = useState<WpSiteOptionsForm>(() => buildWpSiteOptionsForm());
  const [siteIconPickerOpen, setSiteIconPickerOpen] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [generalLoaded, setGeneralLoaded] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [wpSiteError, setWpSiteError] = useState<string | null>(null);
  const [generalSaving, setGeneralSaving] = useState(false);
  const [generalSaveStatus, setGeneralSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [taxConfig, setTaxConfig] = useState<WcTaxConfig | null>(null);
  const [taxForm, setTaxForm] = useState<WcGeneralForm>({});
  const [taxRatesByClass, setTaxRatesByClass] = useState<Record<string, TaxRateDraft[]>>({});
  const [activeTaxClass, setActiveTaxClass] = useState('standard');
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxLoaded, setTaxLoaded] = useState(false);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxSaveStatus, setTaxSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [shipping, setShipping] = useState<WcShippingConfig | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [shippingSettings, setShippingSettings] = useState<WcGeneralSetting[]>([]);
  const [shippingForm, setShippingForm] = useState<WcGeneralForm>({});
  const [shippingAssets, setShippingAssets] = useState<WcShippingAssetsConfig['assets']>({});
  const [shippingRules, setShippingRules] = useState<WcShippingRulesConfig>(EMPTY_SHIPPING_RULES);
  const [zoneDrafts, setZoneDrafts] = useState<Record<number, ZoneDraft>>({});
  const [methodDrafts, setMethodDrafts] = useState<Record<string, MethodDraft>>({});
  const [expandedMethods, setExpandedMethods] = useState<Record<string, boolean>>({});
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneLocations, setNewZoneLocations] = useState<WcShippingZoneLocation[]>([{ type: 'country', code: 'PL' }]);
  const [newMethodByZone, setNewMethodByZone] = useState<Record<number, string>>({});
  const [methodLogoPicker, setMethodLogoPicker] = useState<MethodLogoPickerTarget | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const dragSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const countriesQuery = useQuery({
    queryKey: ['wc-shipping-countries', integrationId],
    queryFn: () => kreatorApi.getWcShippingCountries(integrationId).then(response => response.data),
    enabled: adapter.isTenantScoped,
    staleTime: 24 * 60 * 60 * 1000,
  });
  const countries = countriesQuery.data ?? [];
  const pickupMap = useQuery({
    queryKey: ['pickup-map', storeId],
    queryFn: () => pickupMapsApi.get(storeId!).then(response => response.data),
    enabled: adapter.isTenantScoped && !!storeId && activeTab === 'shipping',
  });
  const hasPickupMap = pickupMap.data?.effective.isActive && pickupMap.data.effective.configured;

  const loadGeneralSettings = useCallback(async () => {
    if (!adapter.isTenantScoped) return;
    setGeneralLoading(true);
    setGeneralError(null);
    setWpSiteError(null);

    const wpSitePromise = kreatorApi.getWpSiteOptions(integrationId)
      .then(response => {
        setWpSiteForm(buildWpSiteOptionsForm(response.data));
      })
      .catch(error => {
        if (!isTransientWordPressError(error)) {
          setWpSiteError(kreatorErrorMessage(error, 'Nie udało się pobrać nazwy strony, sloganu i favicony z WordPress.'));
        }
      });

    try {
      const generalRes = await kreatorApi.getWcGeneralSettings(integrationId);
      setGeneralSettings(generalRes.data.settings);
      setGeneralForm(buildWcGeneralForm(generalRes.data.settings));
      setGeneralLoaded(true);
    } catch (error) {
      const message = kreatorErrorMessage(error, 'Nie udało się pobrać ustawień WooCommerce');
      setGeneralError(message);
    } finally {
      setGeneralLoading(false);
    }
    void wpSitePromise;
  }, [adapter.isTenantScoped, integrationId]);

  const loadShipping = useCallback(async () => {
    if (!adapter.isTenantScoped) return;
    setShippingLoading(true);
    setShippingError(null);
    try {
      const [shippingRes, settingsRes, assetsRes, rulesRes] = await Promise.all([
        kreatorApi.getWcShipping(integrationId),
        kreatorApi.getWcShippingSettings(integrationId).catch(() => ({ data: { settings: [] } })),
        kreatorApi.getWcShippingAssets(integrationId).catch(() => ({ data: { assets: {} } })),
        kreatorApi.getWcShippingRules(integrationId).catch(() => ({ data: EMPTY_SHIPPING_RULES })),
      ]);
      const assets = assetsRes.data.assets ?? {};
      const nextShippingSettings = mergeShippingSettings(settingsRes.data.settings ?? []);
      setShipping(shippingRes.data);
      setShippingSettings(nextShippingSettings);
      setShippingForm(buildWcSettingsForm(nextShippingSettings, WOO_SHIPPING_SETTING_IDS));
      setShippingAssets(assets);
      setShippingRules(normalizeShippingRulesForUi(rulesRes.data));
      setZoneDrafts(buildZoneDrafts(shippingRes.data.zones));
      setMethodDrafts(buildMethodDrafts(shippingRes.data.zones, assets));
    } catch (error) {
      const message = kreatorErrorMessage(error, 'Nie udało się pobrać wysyłki z WooCommerce');
      setShippingError(message);
    } finally {
      setShippingLoading(false);
    }
  }, [adapter.isTenantScoped, integrationId]);

  const loadTaxes = useCallback(async () => {
    if (!adapter.isTenantScoped) return;
    setTaxLoading(true);
    setTaxError(null);
    try {
      const res = await kreatorApi.getWcTaxSettings(integrationId);
      const config = res.data;
      const classes = sortTaxClasses(config.classes);
      const nextConfig = normalizeTaxConfigForUi({ ...config, classes });
      setTaxConfig(nextConfig);
      setTaxForm(buildWcTaxForm(nextConfig));
      setTaxRatesByClass(normalizeTaxRatesByClass(nextConfig));
      if (!classes.some(taxClass => taxClass.slug === activeTaxClass)) {
        setActiveTaxClass(classes[0]?.slug ?? 'standard');
      }
      setTaxLoaded(true);
    } catch (error) {
      const message = kreatorErrorMessage(error, 'Nie udało się pobrać podatków z WooCommerce');
      setTaxError(message);
    } finally {
      setTaxLoading(false);
    }
  }, [activeTaxClass, adapter.isTenantScoped, integrationId]);

  useEffect(() => {
    if (activeTab === 'shipping') {
      void loadShipping();
    }
  }, [activeTab, loadShipping]);

  useEffect(() => {
    if (activeTab !== 'shipping') return;

    const refreshAfterReturningToEcomFlow = () => {
      void loadShipping();
    };
    window.addEventListener('focus', refreshAfterReturningToEcomFlow);
    return () => window.removeEventListener('focus', refreshAfterReturningToEcomFlow);
  }, [activeTab, loadShipping]);

  useEffect(() => {
    if (activeTab === 'general' && !generalLoaded && !generalLoading && !generalError) {
      void loadGeneralSettings();
    }
  }, [activeTab, generalError, generalLoaded, generalLoading, loadGeneralSettings]);

  useEffect(() => {
    if (activeTab === 'taxes' && !taxLoaded && !taxLoading && !taxError) {
      void loadTaxes();
    }
  }, [activeTab, loadTaxes, taxError, taxLoaded, taxLoading]);

  const updateZones = (zones: WcShippingZone[], assets: WcShippingAssetsConfig['assets'] = shippingAssets) => {
    setShipping(prev => prev ? { ...prev, zones } : prev);
    setZoneDrafts(buildZoneDrafts(zones));
    setMethodDrafts(buildMethodDrafts(zones, assets));
  };

  const createZone = async () => {
    if (!newZoneName.trim()) return;
    setBusyKey('zone:new');
    try {
      const res = await kreatorApi.createWcShippingZone(integrationId, {
        name: newZoneName.trim(),
        locations: newZoneLocations,
      });
      updateZones(res.data.zones);
      setNewZoneName('');
      setNewZoneLocations([{ type: 'country', code: 'PL' }]);
      showSuccess('Dodano strefę wysyłki');
    } catch {
      showError('Nie udało się dodać strefy wysyłki');
    } finally {
      setBusyKey(null);
    }
  };

  const deleteZone = async (zone: WcShippingZone) => {
    if (zone.id <= 0) return;
    const confirmed = await confirmDialog({
      title: 'Usuń strefę wysyłki?',
      message: `Usunąć strefę "${zoneDrafts[zone.id]?.name || zone.name}" wraz z jej metodami dostawy?`,
      description: 'Zmiana zostanie zapisana od razu w WooCommerce.',
      confirmLabel: 'Usuń strefę',
      danger: true,
    });
    if (!confirmed) return;

    setBusyKey(`zone:delete:${zone.id}`);
    try {
      const res = await kreatorApi.deleteWcShippingZone(integrationId, zone.id);
      updateZones(res.data.zones);
      showSuccess('Usunięto strefę wysyłki');
    } catch {
      showError('Nie udało się usunąć strefy wysyłki');
    } finally {
      setBusyKey(null);
    }
  };

  const createMethod = async (zoneId: number) => {
    const methodId = newMethodByZone[zoneId];
    if (!methodId) return;
    setBusyKey(`method:new:${zoneId}`);
    try {
      const res = await kreatorApi.createWcShippingZoneMethod(integrationId, zoneId, {
        method_id: methodId,
        enabled: true,
      });
      updateZones(res.data.zones);
      setNewMethodByZone(prev => ({ ...prev, [zoneId]: '' }));
      showSuccess('Dodano metodę dostawy');
    } catch {
      showError('Nie udało się dodać metody dostawy');
    } finally {
      setBusyKey(null);
    }
  };

  const deleteMethod = async (zone: WcShippingZone, method: WcShippingZoneMethod) => {
    const methodName = method.title || method.method_title || method.method_id;
    const confirmed = await confirmDialog({
      title: 'Usunąć metodę dostawy?',
      message: `Metoda „${methodName}” zostanie trwale usunięta ze strefy „${zone.name}”.`,
      description: 'Zmiana zostanie wykonana od razu w WooCommerce. Tej operacji nie można cofnąć.',
      confirmLabel: 'Usuń metodę',
      danger: true,
    });
    if (!confirmed) return;

    const key = methodDraftKey(zone.id, method.instance_id);
    const deleteKey = `method:delete:${zone.id}:${method.instance_id}`;
    setBusyKey(deleteKey);
    try {
      const res = await kreatorApi.deleteWcShippingZoneMethod(
        integrationId,
        zone.id,
        method.instance_id,
      );
      updateZones(res.data.zones);
      setExpandedMethods(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      showSuccess(`Usunięto metodę „${methodName}” z WooCommerce.`);
    } catch (error) {
      showError(kreatorErrorMessage(error, 'Nie udało się usunąć metody dostawy.'));
    } finally {
      setBusyKey(null);
    }
  };

  const selectMethodLogoFromMedia = (url: string) => {
    if (!methodLogoPicker || !shipping) return;
    const zone = shipping.zones.find(item => item.id === methodLogoPicker.zoneId);
    const method = zone?.methods.find(item => item.instance_id === methodLogoPicker.instanceId);
    if (!zone || !method) {
      setMethodLogoPicker(null);
      return;
    }

    const key = methodDraftKey(zone.id, method.instance_id);
    setMethodDrafts(prev => {
      const draft = prev[key] ?? {
        enabled: method.enabled,
        order: method.order ?? 0,
        dispatchEstimate: shippingAssets[shippingMethodAssetKey(method)]?.dispatchEstimate ?? '',
        deliveryKind: shippingAssets[shippingMethodAssetKey(method)]?.deliveryKind ?? 'address',
        pickupProvider: shippingAssets[shippingMethodAssetKey(method)]?.pickupProvider ?? 'plugin',
        pickupPointTypes: shippingAssets[shippingMethodAssetKey(method)]?.pickupPointTypes ?? 'all',
        pickupStorage: shippingAssets[shippingMethodAssetKey(method)]?.pickupStorage ?? 'native',
        logoUrl: shippingAssets[shippingMethodAssetKey(method)]?.logoUrl ?? '',
        logoMaxWidth: shippingLogoMaxWidthInputValue(shippingAssets[shippingMethodAssetKey(method)]?.logoMaxWidth),
        settings: settingValues(method.settings ?? {}),
      };

      return {
        ...prev,
        [key]: {
          ...draft,
          logoUrl: url,
        },
      };
    });
    setMethodLogoPicker(null);
    showSuccess('Wybrano logo z WP Media. Zapisz sekcję, aby opublikować zmianę.');
  };

  const saveGeneral = async () => {
    if (!adapter.isTenantScoped) return;

    setGeneralSaving(true);
    setWpSiteError(null);
    try {
      const [generalResult, wpSiteResult] = await Promise.allSettled([
        kreatorApi.updateWcGeneralSettings(integrationId, generalForm),
        kreatorApi.updateWpSiteOptions(integrationId, {
          siteTitle: wpSiteForm.siteTitle,
          tagline: wpSiteForm.tagline,
          siteIconId: wpSiteForm.siteIconId,
          timezoneString: wpSiteForm.timezoneString,
          permalinkStructure: wpSiteForm.permalinkStructure,
        }),
      ]);

      if (generalResult.status === 'rejected') {
        throw generalResult.reason;
      }

      let wpSiteSaved = true;
      setGeneralSettings(generalResult.value.data.settings);
      setGeneralForm(buildWcGeneralForm(generalResult.value.data.settings));
      if (wpSiteResult.status === 'fulfilled') {
        setWpSiteForm(buildWpSiteOptionsForm(wpSiteResult.value.data));
      } else {
        wpSiteSaved = false;
        const message = kreatorErrorMessage(wpSiteResult.reason, 'Nie udało się zsynchronizować nazwy strony, sloganu i favicony z WordPress.');
        setWpSiteError(message);
        showError(`Ustawienia WooCommerce zapisane, ale WordPress nie przyjął tożsamości strony: ${message}`);
      }

      setGeneralSaveStatus('success');
      setTimeout(() => setGeneralSaveStatus('idle'), 3000);
      if (wpSiteSaved) {
        showSuccess('Zapisano ustawienia WooCommerce');
      }
    } catch (error) {
      const message = kreatorErrorMessage(error, 'Nie udało się zapisać ustawień WooCommerce');
      setGeneralSaveStatus('error');
      setTimeout(() => setGeneralSaveStatus('idle'), 3000);
      showError(message);
    } finally {
      setGeneralSaving(false);
    }
  };

  const saveTaxes = async () => {
    if (!adapter.isTenantScoped || !taxConfig) return;

    const taxesEnabled = wcFormString(taxForm, TAX_ENABLE_SETTING_ID) === 'yes';
    const classesToSave = sortTaxClasses(taxConfig.classes);
    const taxDetailsForSave = taxesEnabled ? mergeTaxDetailSettings(taxConfig.settings) : taxConfig.settings;
    const taxFormForSave = taxesEnabled ? withWcSettingDefaults(taxForm, taxDetailsForSave) : taxForm;
    const canSaveTaxDetails = taxesEnabled && taxDetailsForSave.length > 0;
    setTaxSaving(true);
    try {
      const settingsRes = await kreatorApi.updateWcTaxSettings(integrationId, taxFormForSave);
      const nextConfig = normalizeTaxConfigForUi({ ...settingsRes.data, classes: sortTaxClasses(settingsRes.data.classes) });
      const nextRatesByClass = normalizeTaxRatesByClass(nextConfig);

      if (canSaveTaxDetails) {
        for (const taxClass of classesToSave) {
          const rates = (taxRatesByClass[taxClass.slug] ?? []).map((rate, index) => ({
            ...rate,
            order: index,
            class: taxClass.slug,
            priority: normalizeTaxPriority(rate.priority),
          } satisfies WcTaxRate));
          const ratesRes = await kreatorApi.updateWcTaxRates(integrationId, taxClass.slug, rates);
          nextRatesByClass[taxClass.slug] = ratesRes.data.rates.map(rate => normalizeTaxRate(rate, taxClass.slug));
        }
      }

      setTaxConfig(nextConfig);
      setTaxForm(buildWcTaxForm(nextConfig));
      setTaxRatesByClass(nextRatesByClass);
      setTaxSaveStatus('success');
      setTimeout(() => setTaxSaveStatus('idle'), 3000);
      showSuccess('Zapisano ustawienia podatków');
    } catch (error) {
      const message = kreatorErrorMessage(error, 'Nie udało się zapisać ustawień podatków');
      setTaxSaveStatus('error');
      setTimeout(() => setTaxSaveStatus('idle'), 3000);
      showError(message);
    } finally {
      setTaxSaving(false);
    }
  };

  const saveShipping = async () => {
    if (!shipping) return;
    setSavingShipping(true);
    try {
      let latestZones = shipping.zones;
      const missingMethods: WcShippingZoneMethod[] = [];

      const settingsRes = await kreatorApi.updateWcShippingSettings(integrationId, shippingForm);
      const nextShippingSettings = mergeShippingSettings(settingsRes.data.settings);
      setShippingSettings(nextShippingSettings);
      setShippingForm(buildWcSettingsForm(nextShippingSettings, WOO_SHIPPING_SETTING_IDS));

      for (const zone of shipping.zones) {
        const draft = zoneDrafts[zone.id];
        if (!draft || zone.id === 0) continue;
        const res = await kreatorApi.updateWcShippingZone(integrationId, zone.id, {
          name: draft.name,
          order: draft.order,
          locations: draft.locations,
        });
        latestZones = res.data.zones;
      }

      for (const zone of shipping.zones) {
        for (const method of zone.methods) {
          const key = methodDraftKey(zone.id, method.instance_id);
          const draft = methodDrafts[key];
          if (!draft) continue;
          const settings = shippingSettingsPayload(method, draft);
          try {
            const res = await kreatorApi.updateWcShippingZoneMethod(integrationId, zone.id, method.instance_id, {
              enabled: draft.enabled,
              order: draft.order,
              ...(Object.keys(settings).length > 0 ? { settings } : {}),
            });
            latestZones = res.data.zones;
          } catch (error) {
            if (isMissingWooShippingMethod(error)) {
              missingMethods.push(method);
              continue;
            }
            throw error;
          }
        }
      }

      if (missingMethods.length > 0) {
        latestZones = (await kreatorApi.getWcShipping(integrationId)).data.zones;
      }

      const nextAssets = { ...shippingAssets };
      for (const method of missingMethods) {
        delete nextAssets[shippingMethodAssetKey(method)];
      }
      for (const zone of latestZones) {
        for (const method of zone.methods) {
          const key = methodDraftKey(zone.id, method.instance_id);
          const assetKey = shippingMethodAssetKey(method);
          const logoUrl = methodDrafts[key]?.logoUrl?.trim() ?? '';
          if (logoUrl || methodDrafts[key]?.dispatchEstimate || methodDrafts[key]?.deliveryKind === 'pickup') {
            nextAssets[assetKey] = {
              dispatchEstimate: methodDrafts[key]?.dispatchEstimate?.trim() ?? '',
              deliveryKind: methodDrafts[key]?.deliveryKind ?? 'address',
              pickupProvider: methodDrafts[key]?.pickupProvider ?? 'plugin',
              pickupPointTypes: methodDrafts[key]?.pickupPointTypes ?? 'all',
              pickupStorage: methodDrafts[key]?.pickupStorage ?? 'native',
              logoUrl,
              logoMaxWidth: normalizeShippingLogoMaxWidth(methodDrafts[key]?.logoMaxWidth),
            };
          } else {
            delete nextAssets[assetKey];
          }
        }
      }

      const assetsChanged = JSON.stringify(nextAssets) !== JSON.stringify(shippingAssets);
      const savedAssets = assetsChanged
        ? (await kreatorApi.updateWcShippingAssets(integrationId, { assets: nextAssets })).data.assets
        : shippingAssets;
      const nativeCostRuleIds = new Set(
        latestZones.flatMap(zone => zone.methods
          .filter(hasNativeShippingCostSetting)
          .map(method => methodCostRuleId(method))),
      );
      const missingShippingRateIds = new Set(missingMethods.map(shippingRateId));
      const shippingRulesForSave = {
        ...shippingRules,
        costRules: shippingRules.costRules.filter(rule => (
          !nativeCostRuleIds.has(rule.id)
          && !missingShippingRateIds.has(rule.shippingMethodId ?? '')
        )),
        paymentRules: shippingRules.paymentRules
          .map(rule => ({
            ...rule,
            shippingMethodIds: rule.shippingMethodIds.filter(id => !missingShippingRateIds.has(id)),
          }))
          .filter(rule => rule.shippingMethodIds.length > 0),
      };
      const savedRules = await kreatorApi.updateWcShippingRules(integrationId, shippingRulesPayload(shippingRulesForSave));

      setShippingAssets(savedAssets);
      setShippingRules(normalizeShippingRulesForUi(savedRules.data));
      updateZones(latestZones, savedAssets);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      showSuccess(missingMethods.length > 0
        ? `Zapisano ustawienia i usunięto ${missingMethods.length} nieaktualne metody z widoku.`
        : 'Zapisano ustawienia wysyłki');
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      showError(kreatorErrorMessage(error, 'Nie udało się zapisać ustawień wysyłki'));
    } finally {
      setSavingShipping(false);
    }
  };

  const orderedZones = (): WcShippingZone[] => {
    if (!shipping) return [];
    return [...shipping.zones].sort((a, b) => {
      if (a.id === 0) return 1;
      if (b.id === 0) return -1;
      const aOrder = zoneDrafts[a.id]?.order ?? a.order ?? 0;
      const bOrder = zoneDrafts[b.id]?.order ?? b.order ?? 0;
      return aOrder - bOrder;
    });
  };

  const orderedMethodsForZone = (zone: WcShippingZone): WcShippingZoneMethod[] => (
    [...zone.methods].sort((a, b) => {
      const aOrder = methodDrafts[methodDraftKey(zone.id, a.instance_id)]?.order ?? a.order ?? 0;
      const bOrder = methodDrafts[methodDraftKey(zone.id, b.instance_id)]?.order ?? b.order ?? 0;
      return aOrder - bOrder;
    })
  );

  const shippingMethodOptions: ShippingMethodOption[] = shipping
    ? orderedZones().flatMap(zone => orderedMethodsForZone(zone).map(method => ({
        id: shippingRateId(method),
        label: shippingMethodOptionLabel(zone, method),
      })))
    : [];

  const manualShippingCostRules = shippingRules.costRules.filter(rule => !isMethodCostRule(rule));
  const paymentGateways: WcPaymentGateway[] = shippingRules.paymentGateways ?? [];
  const hiddenPaymentGatewayIds = new Set(shippingRules.hiddenPaymentGatewayIds ?? []);
  const visiblePaymentGateways = paymentGateways.filter(gateway => !hiddenPaymentGatewayIds.has(gateway.id));

  const addShippingCostRule = () => {
    setShippingRules(prev => ({
      ...prev,
      costRules: [...prev.costRules, createShippingCostRule()],
    }));
  };

  const updateShippingCostRule = (ruleId: string, updates: Partial<WcShippingCostRule>) => {
    setShippingRules(prev => ({
      ...prev,
      costRules: prev.costRules.map(rule => rule.id === ruleId ? { ...rule, ...updates } : rule),
    }));
  };

  const methodCostRule = (method: WcShippingZoneMethod): WcShippingCostRule | undefined => (
    shippingRules.costRules.find(rule => rule.id === methodCostRuleId(method))
  );

  const methodCostInputValue = (method: WcShippingZoneMethod, draft: MethodDraft): string => {
    if (hasNativeShippingCostSetting(method)) {
      const settingCost = draft.settings.cost;
      return settingCost === undefined || settingCost === null ? '' : String(settingCost);
    }
    return methodCostRule(method)?.cost ?? '';
  };

  const updateMethodCostRule = (method: WcShippingZoneMethod, cost: string) => {
    const ruleId = methodCostRuleId(method);
    const normalizedCost = cost.trim();
    setShippingRules(prev => {
      const existing = prev.costRules.find(rule => rule.id === ruleId);
      const otherRules = prev.costRules.filter(rule => rule.id !== ruleId);
      if (!normalizedCost) {
        return { ...prev, costRules: otherRules };
      }
      return {
        ...prev,
        costRules: [
          ...otherRules,
          {
            id: ruleId,
            enabled: existing?.enabled ?? true,
            shippingMethodId: shippingRateId(method),
            minSubtotal: '',
            maxSubtotal: '',
            cost: normalizedCost,
          },
        ],
      };
    });
  };

  const updateMethodCost = (method: WcShippingZoneMethod, draft: MethodDraft, key: string, cost: string) => {
    if (hasNativeShippingCostSetting(method)) {
      setMethodDrafts(prev => ({
        ...prev,
        [key]: {
          ...draft,
          settings: { ...draft.settings, cost },
        },
      }));
      return;
    }

    updateMethodCostRule(method, cost);
  };

  const repairFlexibleShippingMethod = async (
    zone: WcShippingZone,
    method: WcShippingZoneMethod,
    draft: MethodDraft,
  ) => {
    const rawCost = methodCostInputValue(method, draft).trim().replace(',', '.');
    const cost = Number(rawCost);
    if (!rawCost || !Number.isFinite(cost) || cost < 0) {
      showError('Najpierw wpisz poprawny koszt dostawy netto.');
      return;
    }

    const confirmed = await confirmDialog({
      title: 'Utworzyć podstawową regułę Flexible Shipping?',
      message: `Metoda będzie dostępna dla każdego koszyka w strefie „${zone.name}” z kosztem ${cost.toFixed(2)} zł netto.`,
      description: 'Poprzednie reguły są uszkodzone i nie można ich odzyskać. Później możesz ustawić bardziej szczegółowe warunki bezpośrednio we wtyczce Flexible Shipping.',
      confirmLabel: 'Utwórz regułę',
      danger: false,
    });
    if (!confirmed) return;

    const repairKey = `method:repair:${zone.id}:${method.instance_id}`;
    setBusyKey(repairKey);
    try {
      const res = await kreatorApi.repairWcFlexibleShippingMethod(
        integrationId,
        zone.id,
        method.instance_id,
        cost,
      );
      updateZones(res.data.zones);
      showSuccess(`Przywrócono metodę „${method.title || method.method_title}”. Sprawdź ją teraz w kasie.`);
    } catch (error) {
      showError(kreatorErrorMessage(error, 'Nie udało się utworzyć reguły Flexible Shipping.'));
    } finally {
      setBusyKey(null);
    }
  };

  const removeShippingCostRule = (ruleId: string) => {
    setShippingRules(prev => ({
      ...prev,
      costRules: prev.costRules.filter(rule => rule.id !== ruleId),
    }));
  };

  const paymentGatewayIdsForShippingMethod = (shippingMethodId: string): string[] => (
    shippingRules.paymentRules
      .filter(rule => rule.shippingMethodIds.includes(shippingMethodId))
      .map(rule => rule.paymentGatewayId)
  );

  const updateShippingMethodPaymentGatewayIds = (shippingMethodId: string, paymentGatewayIds: string[]) => {
    setShippingRules(prev => {
      const selectedGateways = new Set(paymentGatewayIds.filter(Boolean));
      const existingGateways = new Set(prev.paymentRules.map(rule => rule.paymentGatewayId));
      selectedGateways.forEach(gatewayId => existingGateways.add(gatewayId));

      return {
        ...prev,
        paymentRules: Array.from(existingGateways)
          .map(gatewayId => {
            const previousRule = prev.paymentRules.find(rule => rule.paymentGatewayId === gatewayId);
            const shippingMethodIds = previousRule?.shippingMethodIds.filter(id => id !== shippingMethodId) ?? [];

            if (selectedGateways.has(gatewayId)) {
              shippingMethodIds.push(shippingMethodId);
            }

            return {
              paymentGatewayId: gatewayId,
              shippingMethodIds: Array.from(new Set(shippingMethodIds.filter(Boolean))),
            };
          })
          .filter(rule => rule.paymentGatewayId && rule.shippingMethodIds.length > 0),
      };
    });
  };

  const updatePaymentGatewayHidden = (gatewayId: string, hidden: boolean) => {
    setShippingRules(prev => {
      const hiddenIds = new Set(prev.hiddenPaymentGatewayIds ?? []);
      if (hidden) {
        hiddenIds.add(gatewayId);
      } else {
        hiddenIds.delete(gatewayId);
      }

      return {
        ...prev,
        hiddenPaymentGatewayIds: Array.from(hiddenIds),
      };
    });
  };

  const handleZoneDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !shipping) return;

    const movableZones = orderedZones().filter(zone => zone.id > 0);
    const oldIndex = movableZones.findIndex(zone => shippingZoneDragKey(zone.id) === active.id);
    const newIndex = movableZones.findIndex(zone => shippingZoneDragKey(zone.id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(movableZones, oldIndex, newIndex).map((zone, index) => ({
      ...zone,
      order: index,
    }));

    setShipping(prev => prev ? {
      ...prev,
      zones: [
        ...reordered,
        ...prev.zones.filter(zone => zone.id === 0),
      ],
    } : prev);
    setZoneDrafts(prev => {
      const next = { ...prev };
      reordered.forEach((zone, index) => {
        next[zone.id] = {
          ...(prev[zone.id] ?? { name: zone.name, locations: zone.locations ?? [] }),
          order: index,
        };
      });
      return next;
    });
  };

  const handleMethodDragEnd = (zoneId: number, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !shipping) return;
    const zone = shipping.zones.find(item => item.id === zoneId);
    if (!zone) return;
    const orderedMethods = orderedMethodsForZone(zone);
    const oldIndex = orderedMethods.findIndex(method => methodDraftKey(zoneId, method.instance_id) === active.id);
    const newIndex = orderedMethods.findIndex(method => methodDraftKey(zoneId, method.instance_id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(orderedMethods, oldIndex, newIndex).map((method, index) => ({
      ...method,
      order: index,
    }));

    setShipping(prev => prev ? {
      ...prev,
      zones: prev.zones.map(item => item.id === zoneId ? { ...item, methods: reordered } : item),
    } : prev);
    setMethodDrafts(prev => {
      const next = { ...prev };
      reordered.forEach((method, index) => {
        const key = methodDraftKey(zoneId, method.instance_id);
        next[key] = {
          ...(prev[key] ?? {
            enabled: method.enabled,
            dispatchEstimate: shippingAssets[shippingMethodAssetKey(method)]?.dispatchEstimate ?? '',
        deliveryKind: shippingAssets[shippingMethodAssetKey(method)]?.deliveryKind ?? 'address',
        pickupProvider: shippingAssets[shippingMethodAssetKey(method)]?.pickupProvider ?? 'plugin',
        pickupPointTypes: shippingAssets[shippingMethodAssetKey(method)]?.pickupPointTypes ?? 'all',
        pickupStorage: shippingAssets[shippingMethodAssetKey(method)]?.pickupStorage ?? 'native',
        logoUrl: shippingAssets[shippingMethodAssetKey(method)]?.logoUrl ?? '',
            logoMaxWidth: shippingLogoMaxWidthInputValue(shippingAssets[shippingMethodAssetKey(method)]?.logoMaxWidth),
            settings: settingValues(method.settings ?? {}),
          }),
          order: index,
        };
      });
      return next;
    });
  };

  const wcSetting = (id: string) => findWcSetting(generalSettings, id);
  const shippingSetting = (id: string) => findWcSetting(shippingSettings, id);
  const taxesEnabled = wcFormString(taxForm, TAX_ENABLE_SETTING_ID) === 'yes';
  const taxDetailSettings = taxesEnabled ? mergeTaxDetailSettings(taxConfig?.settings ?? []) : (taxConfig?.settings ?? []);
  const taxFormWithDefaults = taxesEnabled ? withWcSettingDefaults(taxForm, taxDetailSettings) : taxForm;
  const taxSettings = taxConfig ? [
    ...(taxConfig.enableTaxes ? [taxConfig.enableTaxes] : []),
    ...taxDetailSettings,
  ] : [];
  const taxSetting = (id: string) => findWcSetting(taxSettings, id);
  const taxDetailsAvailable = taxDetailSettings.length > 0;
  const taxClasses = sortTaxClasses(taxConfig?.classes ?? []);
  const activeTaxClassConfig = taxClasses.find(taxClass => taxClass.slug === activeTaxClass) ?? taxClasses[0];
  const countryOptions = countries.map(country => ({ value: country.code, label: `${country.name} (${country.code})` }));
  const defaultCountryValue = wcFormString(generalForm, 'woocommerce_default_country');
  const defaultCountryOptions = defaultCountryValue && !countryOptions.some(option => option.value === defaultCountryValue)
    ? [{ value: defaultCountryValue, label: defaultCountryValue }, ...countryOptions]
    : countryOptions;
  const permalinkOptions = wpSiteForm.permalinkStructure && !PERMALINK_OPTIONS.some(option => option.value === wpSiteForm.permalinkStructure)
    ? [{ value: wpSiteForm.permalinkStructure, label: `Własny: ${wpSiteForm.permalinkStructure}` }, ...PERMALINK_OPTIONS]
    : PERMALINK_OPTIONS;
  const allowedCountriesMode = wcFormString(generalForm, 'woocommerce_allowed_countries');
  const shipToCountriesMode = wcFormString(generalForm, 'woocommerce_ship_to_countries');

  return (
    <div className="space-y-6 pb-20">
      <PageHeader
        icon={ShoppingBag}
        title="Sklep"
        subtitle="Ustawienia WooCommerce zarządzane z poziomu EcomFlow"
      />

      {!adapter.isTenantScoped && (
        <Alert variant="warning" title="Wybierz konkretny sklep">
          Wysyłka jest dostępna tylko w ustawieniach sklepu, bo wymaga połączenia z WooCommerce.
        </Alert>
      )}

      {adapter.isTenantScoped && (
        <>
          <div className="flex w-fit max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-border-subtle bg-surface-raised/50 p-1">
            <button
              type="button"
              className={tabButtonClass(activeTab === 'general')}
              onClick={() => setActiveTab('general')}
            >
              <Settings2 className="h-4 w-4" />
              Ogólne
            </button>
            <button
              type="button"
              className={tabButtonClass(activeTab === 'taxes')}
              onClick={() => setActiveTab('taxes')}
            >
              <Percent className="h-4 w-4" />
              Podatki
            </button>
            <button
              type="button"
              className={tabButtonClass(activeTab === 'shipping')}
              onClick={() => setActiveTab('shipping')}
            >
              <Truck className="h-4 w-4" />
              Przesyłka
            </button>
            <button type="button" className={tabButtonClass(activeTab === 'payments')} onClick={() => setActiveTab('payments')}><CreditCard className="h-4 w-4" />Płatności</button>
          </div>

          {activeTab === 'payments' && <KreatorPaymentsSection key={integrationId} integrationId={integrationId} wordpressUrl={wordpressUrl} />}
          {activeTab === 'general' && (
            <>
              {generalError && (
                <Alert variant="danger" title="Nie udało się pobrać ustawień ogólnych">
                  {generalError}
                </Alert>
              )}

              {generalLoading && !generalLoaded ? (
                <div className="flex items-center justify-center rounded-card border border-border-subtle bg-surface-base/40 py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-accent-primary" />
                </div>
              ) : (
                <>
                  <div className="space-y-6">
                    <SettingsSection
                      icon={Settings2}
                      title="Tożsamość strony"
                      subtitle="Nazwa, slogan, permalink i favicon zapisywane w WordPressie."
                      actions={
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={loadGeneralSettings}
                          loading={generalLoading}
                          leftIcon={<RefreshCw />}
                        >
                          Odśwież
                        </Button>
                      }
                    >
                      <div className="space-y-4">
                        {wpSiteError && (
                          <Alert variant="warning" title="WordPress nie zwrócił tożsamości strony">
                            {wpSiteError}
                          </Alert>
                        )}

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <FormField label="Nazwa strony" htmlFor="wp-site-title" required>
                            <Input
                              id="wp-site-title"
                              value={wpSiteForm.siteTitle}
                              onChange={(event) => setWpSiteForm(prev => ({ ...prev, siteTitle: event.target.value }))}
                              placeholder="Nazwa sklepu"
                            />
                          </FormField>
                          <FormField label="Slogan" htmlFor="wp-site-tagline">
                            <Input
                              id="wp-site-tagline"
                              value={wpSiteForm.tagline}
                              onChange={(event) => setWpSiteForm(prev => ({ ...prev, tagline: event.target.value }))}
                              placeholder="Krótki opis sklepu"
                            />
                          </FormField>
                          <FormField label="Strefa czasowa" htmlFor="wp-site-timezone">
                            <Input
                              id="wp-site-timezone"
                              value={wpSiteForm.timezoneString}
                              onChange={(event) => setWpSiteForm(prev => ({ ...prev, timezoneString: event.target.value }))}
                              placeholder="Europe/Warsaw"
                            />
                          </FormField>
                          <FormField label="Struktura permalinków" htmlFor="wp-site-permalinks">
                            <Select
                              id="wp-site-permalinks"
                              value={wpSiteForm.permalinkStructure}
                              onChange={(event) => setWpSiteForm(prev => ({ ...prev, permalinkStructure: event.target.value }))}
                            >
                              {permalinkOptions.map(option => (
                                <option key={option.value || 'plain'} value={option.value}>{option.label}</option>
                              ))}
                            </Select>
                          </FormField>
                        </div>

                        <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div className="flex min-w-0 items-center gap-4">
                              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border-subtle bg-surface-raised">
                                {wpSiteForm.siteIconUrl ? (
                                  <img src={wpSiteForm.siteIconUrl} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <ImageIcon className="h-6 w-6 text-text-muted" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-text-primary">Favicon / ikona witryny</div>
                                <p className="mt-1 text-xs text-text-muted">
                                  Ikona jest zapisywana jako WordPress site icon i używana jako favicon.
                                </p>
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-wrap gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setSiteIconPickerOpen(true)}
                                leftIcon={<ImageIcon />}
                              >
                                Wybierz ikonę
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={!wpSiteForm.siteIconId && !wpSiteForm.siteIconUrl}
                                onClick={() => setWpSiteForm(prev => ({ ...prev, siteIconId: null, siteIconUrl: '' }))}
                                leftIcon={<X />}
                              >
                                Usuń
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </SettingsSection>

                    <SettingsSection
                      icon={MapPinned}
                      title="Adres sklepu"
                      subtitle="Adres bazowy WooCommerce używany do podatków i wysyłki."
                    >
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <WcTextField
                          setting={wcSetting('woocommerce_store_address')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Adres wiersz 1"
                        />
                        <WcTextField
                          setting={wcSetting('woocommerce_store_address_2')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Adres wiersz 2"
                        />
                        <WcTextField
                          setting={wcSetting('woocommerce_store_city')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Miejscowość"
                        />
                        <WcSelectField
                          setting={wcSetting('woocommerce_default_country')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Kraj"
                          labelOverride="Kraj"
                          fallbackOptions={defaultCountryOptions}
                        />
                        <WcTextField
                          setting={wcSetting('woocommerce_store_postcode')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Kod pocztowy"
                        />
                      </div>
                    </SettingsSection>

                    <SettingsSection
                      icon={ShoppingBag}
                      title="Sprzedaż i wysyłka"
                      subtitle="Kraje sprzedaży, kraje wysyłki, domyślna lokalizacja klienta i kupony."
                    >
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <WcSelectField
                          setting={wcSetting('woocommerce_allowed_countries')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Lokalizacja sprzedaży"
                        />
                        {allowedCountriesMode === 'specific' && (
                          <FormField label="Sprzedawaj do wybranych krajów" htmlFor="wc-general-specific-allowed-countries">
                            <WcCountryCodeSelectionField
                              id="wc-general-specific-allowed-countries"
                              value={wcFormArray(generalForm, 'woocommerce_specific_allowed_countries')}
                              countries={countries}
                              onChange={(codes) => updateWcFormValue(setGeneralForm, 'woocommerce_specific_allowed_countries', codes)}
                            />
                          </FormField>
                        )}
                        {allowedCountriesMode === 'all_except' && wcSetting('woocommerce_all_except_countries') && (
                          <FormField label="Kraje wykluczone ze sprzedaży" htmlFor="wc-general-all-except-countries">
                            <WcCountryCodeSelectionField
                              id="wc-general-all-except-countries"
                              value={wcFormArray(generalForm, 'woocommerce_all_except_countries')}
                              countries={countries}
                              onChange={(codes) => updateWcFormValue(setGeneralForm, 'woocommerce_all_except_countries', codes)}
                            />
                          </FormField>
                        )}
                        <WcSelectField
                          setting={wcSetting('woocommerce_ship_to_countries')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Lokalizacje wysyłki"
                        />
                        {shipToCountriesMode === 'specific' && (
                          <FormField label="Wysyłaj do wybranych krajów" htmlFor="wc-general-specific-ship-to-countries">
                            <WcCountryCodeSelectionField
                              id="wc-general-specific-ship-to-countries"
                              value={wcFormArray(generalForm, 'woocommerce_specific_ship_to_countries')}
                              countries={countries}
                              onChange={(codes) => updateWcFormValue(setGeneralForm, 'woocommerce_specific_ship_to_countries', codes)}
                            />
                          </FormField>
                        )}
                        <WcSelectField
                          setting={wcSetting('woocommerce_default_customer_address')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Domyślna lokalizacja klienta"
                        />
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <WcToggleField
                          setting={wcSetting('woocommerce_enable_address_autocomplete')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Autouzupełnianie adresu"
                        />
                        <WcToggleField
                          setting={wcSetting('woocommerce_enable_coupons')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Włącz kupony"
                        />
                        <WcToggleField
                          setting={wcSetting('woocommerce_calc_discounts_sequentially')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Oblicz rabaty kuponów kolejno"
                        />
                      </div>
                    </SettingsSection>

                    <SettingsSection
                      icon={Settings2}
                      title="Waluta"
                      subtitle="Format cen i liczba miejsc po przecinku w WooCommerce."
                    >
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <WcSelectField
                          setting={wcSetting('woocommerce_currency')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Waluta"
                        />
                        <WcSelectField
                          setting={wcSetting('woocommerce_currency_pos')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Pozycja symbolu waluty"
                        />
                        <WcTextField
                          setting={wcSetting('woocommerce_price_thousand_sep')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Separator tysięczny"
                        />
                        <WcTextField
                          setting={wcSetting('woocommerce_price_decimal_sep')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Separator dziesiętny"
                        />
                        <WcTextField
                          setting={wcSetting('woocommerce_price_num_decimals')}
                          form={generalForm}
                          setForm={setGeneralForm}
                          fallbackLabel="Liczba cyfr po przecinku"
                          type="number"
                        />
                      </div>
                    </SettingsSection>
                  </div>

                  <SettingsSaveBar
                    saving={generalSaving}
                    status={generalSaveStatus}
                    onSave={saveGeneral}
                    label="Zapisz ogólne"
                    successLabel="Zapisano ogólne"
                    errorLabel="Błąd zapisu ogólnych"
                  />
                </>
              )}
            </>
          )}

          {activeTab === 'taxes' && (
            <>
              {taxError && (
                <Alert variant="danger" title="Nie udało się pobrać podatków">
                  {taxError}
                </Alert>
              )}

              {taxLoading && !taxLoaded ? (
                <div className="flex items-center justify-center rounded-card border border-border-subtle bg-surface-base/40 py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-accent-primary" />
                </div>
              ) : !taxConfig ? (
                <SettingsSection
                  icon={Percent}
                  title="Podatki WooCommerce"
                  subtitle="Włączanie podatków, sposób naliczania i stawki zapisane w WooCommerce."
                  actions={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={loadTaxes}
                      loading={taxLoading}
                      leftIcon={<RefreshCw />}
                    >
                      Odśwież
                    </Button>
                  }
                >
                  <div className="rounded-card border border-border-subtle bg-surface-base/40 px-4 py-6 text-sm text-text-muted">
                    Brak danych podatków z WooCommerce. Odśwież sekcję albo sprawdź połączenie integracji.
                  </div>
                </SettingsSection>
              ) : (
                <>
                  <SettingsSection
                    icon={Percent}
                    title="Podatki WooCommerce"
                    subtitle="Włączanie podatków, sposób naliczania i stawki zapisane w WooCommerce."
                    actions={
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={loadTaxes}
                        loading={taxLoading}
                        leftIcon={<RefreshCw />}
                      >
                        Odśwież
                      </Button>
                    }
                  >
                    <div className="space-y-6">
                      <WcToggleField
                        setting={taxSetting(TAX_ENABLE_SETTING_ID)}
                        form={taxForm}
                        setForm={setTaxForm}
                        fallbackLabel="Włącz podatki"
                        labelOverride="Włącz podatki"
                      />

                      {taxesEnabled && taxDetailsAvailable && (
                        <>
                          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                            <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                              <h3 className="mb-4 text-sm font-semibold text-text-primary">Naliczanie podatku</h3>
                              <div className="grid grid-cols-1 gap-4">
                                <WcSelectField
                                  setting={taxSetting('woocommerce_prices_include_tax')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Ceny wpisywane z podatkiem"
                                />
                                <WcSelectField
                                  setting={taxSetting('woocommerce_tax_based_on')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Obliczaj podatek w oparciu o"
                                />
                                <WcSelectField
                                  setting={taxSetting('woocommerce_shipping_tax_class')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Klasa podatkowa wysyłki"
                                />
                                <WcToggleField
                                  setting={taxSetting('woocommerce_tax_round_at_subtotal')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Zaokrąglaj podatek na sumie częściowej"
                                />
                              </div>
                            </div>

                            <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                              <h3 className="mb-4 text-sm font-semibold text-text-primary">Wyświetlanie cen</h3>
                              <div className="grid grid-cols-1 gap-4">
                                <WcSelectField
                                  setting={taxSetting('woocommerce_tax_display_shop')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Wyświetlanie cen w sklepie"
                                />
                                <WcSelectField
                                  setting={taxSetting('woocommerce_tax_display_cart')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Wyświetlanie cen w koszyku i zamówieniu"
                                />
                                <WcTextField
                                  setting={taxSetting('woocommerce_price_display_suffix')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Sufiks wyświetlanej ceny"
                                />
                                <WcSelectField
                                  setting={taxSetting('woocommerce_tax_total_display')}
                                  form={taxFormWithDefaults}
                                  setForm={setTaxForm}
                                  fallbackLabel="Wyświetlanie sumy podatków"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                            <h3 className="mb-4 text-sm font-semibold text-text-primary">Klasy podatkowe</h3>
                            <WcTextareaField
                              setting={taxSetting('woocommerce_tax_classes')}
                              form={taxFormWithDefaults}
                              setForm={setTaxForm}
                              fallbackLabel="Dodatkowe klasy podatkowe"
                              minRows={3}
                            />
                          </div>

                          <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                            <div className="mb-4 flex flex-wrap items-center gap-2">
                              {taxClasses.map(taxClass => (
                                <button
                                  key={taxClass.slug}
                                  type="button"
                                  className={tabButtonClass((activeTaxClassConfig?.slug ?? activeTaxClass) === taxClass.slug)}
                                  onClick={() => setActiveTaxClass(taxClass.slug)}
                                >
                                  {taxClassLabel(taxClass)}
                                </button>
                              ))}
                            </div>

                            {activeTaxClassConfig ? (
                              <TaxRateTable
                                taxClass={activeTaxClassConfig}
                                rates={taxRatesByClass[activeTaxClassConfig.slug] ?? []}
                                onChange={(rates) => setTaxRatesByClass(prev => ({
                                  ...prev,
                                  [activeTaxClassConfig.slug]: rates,
                                }))}
                              />
                            ) : (
                              <div className="rounded-card border border-dashed border-border-subtle px-4 py-8 text-center text-sm text-text-muted">
                                Brak klas podatkowych do edycji.
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </SettingsSection>

                  <SettingsSaveBar
                    saving={taxSaving}
                    status={taxSaveStatus}
                    onSave={saveTaxes}
                    label="Zapisz podatki"
                    successLabel="Zapisano podatki"
                    errorLabel="Błąd zapisu podatków"
                  />
                </>
              )}
            </>
          )}

          {activeTab === 'shipping' && (
            <>
              <SettingsSection
                icon={Truck}
                title="Wysyłka WooCommerce"
                subtitle="Konfiguruj sposób dostawy, dostępność metod i ich prezentację w sklepie."
                actions={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={loadShipping}
                    loading={shippingLoading}
                    leftIcon={<RefreshCw />}
                  >
                    Odśwież z WooCommerce
                  </Button>
                }
              >
          <div className="space-y-6">
            {shippingError && (
              <Alert variant="danger" title="Nie udało się pobrać wysyłki">
                {shippingError}
              </Alert>
            )}
            {countriesQuery.isError && (
              <Alert variant="warning" title="Nie udało się pobrać listy krajów">
                Lista państw jest pobierana z WooCommerce. Odśwież sekcję albo sprawdź połączenie ze sklepem.
              </Alert>
            )}

            {shippingLoading && !shipping && (
              <div className="flex items-center justify-center rounded-card border border-border-subtle bg-surface-base/40 py-16">
                <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
              </div>
            )}

            {shipping && (
              <>
                <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-primary/10 text-accent-primary">
                      <Truck className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary">Ustawienia wysyłki</h3>
                      <p className="mt-0.5 text-xs text-text-muted">Globalne zachowanie kalkulacji i adresu wysyłki w WooCommerce.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <WcToggleField
                      setting={shippingSetting('woocommerce_enable_shipping_calc')}
                      form={shippingForm}
                      setForm={setShippingForm}
                      fallbackLabel="Kalkulator wysyłki w koszyku"
                    />
                    <WcToggleField
                      setting={shippingSetting('woocommerce_shipping_cost_requires_address')}
                      form={shippingForm}
                      setForm={setShippingForm}
                      fallbackLabel="Ukryj koszty przed wpisaniem adresu"
                    />
                    <WcToggleField
                      setting={shippingSetting('woocommerce_shipping_hide_rates_when_free')}
                      form={shippingForm}
                      setForm={setShippingForm}
                      fallbackLabel="Ukryj płatne metody przy darmowej wysyłce"
                    />
                    <WcSelectField
                      setting={shippingSetting('woocommerce_ship_to_destination')}
                      form={shippingForm}
                      setForm={setShippingForm}
                      fallbackLabel="Domyślny adres wysyłki"
                    />
                  </div>
                </div>

                <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                  <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-primary/10 text-accent-primary">
                        <Percent className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-text-primary">Reguły kosztów wysyłki</h3>
                        <p className="mt-0.5 text-xs text-text-muted">Ustal koszt dostawy netto zależnie od wartości koszyka netto i wybranej metody.</p>
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={addShippingCostRule}
                      leftIcon={<Plus />}
                    >
                      Dodaj regułę
                    </Button>
                  </div>

                  {manualShippingCostRules.length === 0 ? (
                    <div className="rounded-card border border-dashed border-border-subtle px-4 py-6 text-center text-sm text-text-muted">
                      Brak reguł kosztu wysyłki.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {manualShippingCostRules.map((rule, index) => (
                        <div
                          key={rule.id}
                          className="grid grid-cols-1 gap-3 rounded-card border border-border-subtle bg-surface-raised/35 p-4 xl:grid-cols-[auto_minmax(220px,1.1fr)_minmax(120px,.7fr)_minmax(120px,.7fr)_minmax(120px,.7fr)_auto] xl:items-end"
                        >
                          <label className="flex items-center gap-2 rounded-md border border-border-subtle bg-surface-base/50 px-3 py-2 text-sm text-text-primary xl:mb-0.5">
                            <input
                              type="checkbox"
                              checked={rule.enabled}
                              onChange={(event) => updateShippingCostRule(rule.id, { enabled: event.target.checked })}
                              className="h-4 w-4 rounded border-border-subtle bg-surface-raised text-accent-primary focus:ring-accent-primary"
                            />
                            Aktywna
                          </label>
                          <FormField label={`Warunek ${index + 1}`} htmlFor={`shipping-cost-method-${rule.id}`}>
                            <Select
                              id={`shipping-cost-method-${rule.id}`}
                              value={rule.shippingMethodId ?? ''}
                              onChange={(event) => updateShippingCostRule(rule.id, { shippingMethodId: event.target.value })}
                            >
                              <option value="">Wszystkie płatne metody</option>
                              {shippingMethodOptions.map(method => (
                                <option key={method.id} value={method.id}>{method.label}</option>
                              ))}
                            </Select>
                          </FormField>
                          <FormField label="Koszyk netto od" htmlFor={`shipping-cost-min-${rule.id}`}>
                            <Input
                              id={`shipping-cost-min-${rule.id}`}
                              inputMode="decimal"
                              value={rule.minSubtotal ?? ''}
                              onChange={(event) => updateShippingCostRule(rule.id, { minSubtotal: event.target.value })}
                              placeholder="0"
                            />
                          </FormField>
                          <FormField label="Koszyk netto do" htmlFor={`shipping-cost-max-${rule.id}`}>
                            <Input
                              id={`shipping-cost-max-${rule.id}`}
                              inputMode="decimal"
                              value={rule.maxSubtotal ?? ''}
                              onChange={(event) => updateShippingCostRule(rule.id, { maxSubtotal: event.target.value })}
                              placeholder="Bez limitu"
                            />
                          </FormField>
                          <FormField label="Koszt netto" htmlFor={`shipping-cost-value-${rule.id}`}>
                            <Input
                              id={`shipping-cost-value-${rule.id}`}
                              inputMode="decimal"
                              value={rule.cost}
                              onChange={(event) => updateShippingCostRule(rule.id, { cost: event.target.value })}
                              placeholder="0"
                            />
                          </FormField>
                          <Button
                            variant="icon"
                            leftIcon={<Trash2 />}
                            onClick={() => removeShippingCostRule(rule.id)}
                            aria-label="Usuń regułę kosztu wysyłki"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-primary/10 text-accent-primary">
                      <CreditCard className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary">Płatności dla metod dostawy</h3>
                      <p className="mt-0.5 text-xs text-text-muted">
                        Metody dostawy pozostają widoczne. Przypisana bramka płatności pojawi się tylko przy wybranych dostawach, a bramka bez przypisań działa globalnie.
                      </p>
                    </div>
                  </div>

                  {paymentGateways.length === 0 ? (
                    <div className="rounded-card border border-dashed border-border-subtle px-4 py-6 text-center text-sm text-text-muted">
                      Brak aktywnych bramek płatności do powiązania.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="rounded-card border border-border-subtle bg-surface-raised/35 p-4">
                        <div className="mb-3">
                          <h4 className="text-sm font-semibold text-text-primary">Widoczność bramek płatności</h4>
                          <p className="mt-0.5 text-xs text-text-muted">
                            Ukryta bramka nie pojawi się klientowi w kasie, niezależnie od wybranej dostawy.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                          {paymentGateways.map(gateway => {
                            const hidden = hiddenPaymentGatewayIds.has(gateway.id);
                            return (
                              <div
                                key={`visibility:${gateway.id}`}
                                className="flex min-h-[48px] items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-base/50 px-3 py-2"
                              >
                                <div className="min-w-0">
                                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-medium text-text-primary">{gateway.title || gateway.id}</span>
                                    <Badge variant="neutral" size="sm">{gateway.id}</Badge>
                                    {hidden && <Badge variant="danger" size="sm">Ukryta</Badge>}
                                  </div>
                                </div>
                                <Button
                                  variant={hidden ? 'secondary' : 'ghost'}
                                  size="sm"
                                  onClick={() => updatePaymentGatewayHidden(gateway.id, !hidden)}
                                >
                                  {hidden ? 'Pokaż' : 'Ukryj'}
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {shippingMethodOptions.length === 0 ? (
                        <div className="rounded-card border border-dashed border-border-subtle px-4 py-6 text-center text-sm text-text-muted">
                          Dodaj metody dostawy, aby powiązać je z bramkami płatności.
                        </div>
                      ) : visiblePaymentGateways.length === 0 ? (
                        <div className="rounded-card border border-dashed border-border-subtle px-4 py-6 text-center text-sm text-text-muted">
                          Wszystkie bramki płatności są ukryte.
                        </div>
                      ) : null}

                      {shippingMethodOptions.length > 0 && visiblePaymentGateways.length > 0 && shippingMethodOptions.map(method => {
                        const selectedGatewayIds = paymentGatewayIdsForShippingMethod(method.id);
                        return (
                          <div key={method.id} className="rounded-card border border-border-subtle bg-surface-raised/35 p-4">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                              <h4 className="text-sm font-semibold text-text-primary">{method.label}</h4>
                              <Badge variant="neutral" size="sm">{method.id}</Badge>
                            </div>
                            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                              {visiblePaymentGateways.map(gateway => {
                                const checked = selectedGatewayIds.includes(gateway.id);
                                return (
                                  <label
                                    key={`${method.id}:${gateway.id}`}
                                    className="flex min-h-[44px] items-center gap-2 rounded-md border border-border-subtle bg-surface-base/50 px-3 py-2 text-sm text-text-primary"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(event) => {
                                        updateShippingMethodPaymentGatewayIds(
                                          method.id,
                                          event.target.checked
                                            ? [...selectedGatewayIds, gateway.id]
                                            : selectedGatewayIds.filter(id => id !== gateway.id),
                                        );
                                      }}
                                      className="h-4 w-4 rounded border-border-subtle bg-surface-raised text-accent-primary focus:ring-accent-primary"
                                    />
                                    <span className="min-w-0 truncate">{gateway.title || gateway.id}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-primary/10 text-accent-primary">
                      <MapPinned className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-text-primary">Nowa strefa dostawy</h3>
                      <p className="mt-0.5 text-xs text-text-muted">Dodaj strefę i wybierz kraje dostępne w WooCommerce.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_auto] lg:items-start">
                    <FormField label="Nazwa strefy" htmlFor="new-shipping-zone-name">
                      <Input
                        id="new-shipping-zone-name"
                        value={newZoneName}
                        onChange={(event) => setNewZoneName(event.target.value)}
                        placeholder="np. Polska"
                      />
                    </FormField>
                    <FormField
                      label="Lokalizacja"
                      htmlFor="new-shipping-zone-locations"
                      helper="Wybierz kraje dostępne w WooCommerce."
                    >
                      <CountrySelectionField
                        id="new-shipping-zone-locations"
                        value={newZoneLocations}
                        countries={countries}
                        onChange={setNewZoneLocations}
                      />
                    </FormField>
                    <Button
                      className="lg:mt-6"
                      onClick={createZone}
                      disabled={!newZoneName.trim()}
                      loading={busyKey === 'zone:new'}
                      leftIcon={<Plus />}
                    >
                      Dodaj strefę
                    </Button>
                  </div>
                </div>

                <DndContext
                  sensors={dragSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleZoneDragEnd}
                >
                  <SortableContext
                    items={orderedZones().map(zone => shippingZoneDragKey(zone.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {orderedZones().map(zone => {
                        const zoneDraft = zoneDrafts[zone.id];
                        return (
                          <SortableShippingZone
                            key={zone.id}
                            id={shippingZoneDragKey(zone.id)}
                            title={zoneDraft?.name || zone.name}
                            subtitle={zone.id === 0 ? 'Strefa domyślna WooCommerce jest zawsze na końcu.' : 'Przeciągnij uchwyt, aby zmienić kolejność stref.'}
                            disabled={zone.id === 0}
                            badges={
                              <>
                                <Badge variant={zone.id === 0 ? 'neutral' : 'accent'} size="sm">
                                  {zone.id === 0 ? 'Domyślna' : `ID ${zone.id}`}
                                </Badge>
                                <Badge variant="info" size="sm">
                                  {zone.methods.length} metod
                                </Badge>
                              </>
                            }
                            actions={zone.id > 0 ? (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => void deleteZone(zone)}
                                loading={busyKey === `zone:delete:${zone.id}`}
                                leftIcon={<Trash2 />}
                              >
                                Usuń
                              </Button>
                            ) : undefined}
                          >

                        <div className="space-y-5 p-4">
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                            <FormField label="Strefa" htmlFor={`zone-name-${zone.id}`}>
                              <Input
                                id={`zone-name-${zone.id}`}
                                value={zoneDraft?.name ?? zone.name}
                                disabled={zone.id === 0}
                                onChange={(event) => setZoneDrafts(prev => ({
                                  ...prev,
                                  [zone.id]: { ...(prev[zone.id] ?? { order: zone.order, locations: zone.locations ?? [] }), name: event.target.value },
                                }))}
                              />
                            </FormField>
                            <FormField
                              label="Lokalizacja"
                              htmlFor={`zone-locations-${zone.id}`}
                              helper={zone.id === 0 ? 'Strefa domyślna WooCommerce nie ma ręcznie wybieranych krajów.' : 'Wybierz kraje dostępne w WooCommerce.'}
                            >
                              <CountrySelectionField
                                id={`zone-locations-${zone.id}`}
                                value={zoneDraft?.locations ?? zone.locations ?? []}
                                countries={countries}
                                disabled={zone.id === 0}
                                onChange={(locations) => setZoneDrafts(prev => ({
                                  ...prev,
                                  [zone.id]: { ...(prev[zone.id] ?? { name: zone.name, order: zone.order }), locations },
                                }))}
                              />
                            </FormField>
                          </div>

                          <div className="rounded-card border border-border-subtle bg-surface-raised/35 p-4">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                              <FormField label="Dodaj metodę dostawy" htmlFor={`new-method-${zone.id}`}>
                                <Select
                                  id={`new-method-${zone.id}`}
                                  value={newMethodByZone[zone.id] ?? ''}
                                  onChange={(event) => setNewMethodByZone(prev => ({ ...prev, [zone.id]: event.target.value }))}
                                >
                                  <option value="">Wybierz metodę</option>
                                  {shipping.availableMethods.filter(method => isCoreShippingMethod(method.id)).map(method => (
                                    <option key={method.id} value={method.id}>{method.title || method.id}</option>
                                  ))}
                                </Select>
                              </FormField>
                              <Button
                                variant="secondary"
                                onClick={() => createMethod(zone.id)}
                                disabled={!newMethodByZone[zone.id]}
                                loading={busyKey === `method:new:${zone.id}`}
                                leftIcon={<Plus />}
                              >
                                Dodaj metodę
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3">
                            {zone.methods.length === 0 && (
                              <div className="rounded-card border border-dashed border-border-subtle px-4 py-6 text-center text-sm text-text-muted">
                                Brak metod dostawy w tej strefie.
                              </div>
                            )}

                            {zone.methods.length > 0 && (
                              <DndContext
                                sensors={dragSensors}
                                collisionDetection={closestCenter}
                                onDragEnd={(event) => handleMethodDragEnd(zone.id, event)}
                              >
                                <SortableContext
                                  items={orderedMethodsForZone(zone).map(method => methodDraftKey(zone.id, method.instance_id))}
                                  strategy={verticalListSortingStrategy}
                                >
                                  {orderedMethodsForZone(zone).map(method => {
                                    const key = methodDraftKey(zone.id, method.instance_id);
                                    const draft = methodDrafts[key] ?? {
                                      enabled: method.enabled,
                                      order: method.order ?? 0,
                                      logoUrl: '',
                                      logoMaxWidth: String(SHIPPING_LOGO_WIDTH_DEFAULT),
                                      settings: settingValues(method.settings ?? {}),
                                    };
                                    const expanded = expandedMethods[key] ?? false;
                                    const isExternalMethod = !isCoreShippingMethod(method.method_id);
                                    const invalidFlexibleShippingRules = hasInvalidFlexibleShippingRules(method);
                                    const externalSettingsUrl = wordpressUrl
                                      ? `${wordpressUrl.replace(/\/$/, '')}/wp-admin/admin.php?page=wc-settings&tab=shipping&instance_id=${method.instance_id}`
                                      : null;
                                    const cleanSettings = cleanShippingSettings(method);
                                    const showCostField = shouldShowShippingCostField(method);
                                    return (
                                      <SortableShippingMethod
                                        key={key}
                                        id={key}
                                        title={method.title || method.method_title || method.method_id}
                                        meta={`${method.method_id}:${method.instance_id}`}
                                        enabled={draft.enabled}
                                        expanded={expanded}
                                        external={isExternalMethod}
                                        onToggle={() => setExpandedMethods(prev => ({ ...prev, [key]: !expanded }))}
                                      >
                                        <ToggleRow
                                          checked={draft.enabled}
                                          onChange={(checked) => setMethodDrafts(prev => ({
                                            ...prev,
                                            [key]: { ...draft, enabled: checked },
                                          }))}
                                          label="Metoda aktywna"
                                          description="Włącza tę metodę dostawy w WooCommerce."
                                          className="rounded-card border border-border-subtle bg-surface-base/40 border-t"
                                        />

                                        {invalidFlexibleShippingRules && (
                                          <Alert variant="danger" title="Flexible Shipping nie ma poprawnych reguł">
                                            <div className="space-y-3 text-sm">
                                              <p>
                                                Metoda jest włączona w strefie, ale sama kwota poniżej nie tworzy reguły wymaganej przez wtyczkę. Dlatego Paczkomat nie pojawia się w kasie.
                                              </p>
                                              <div className="flex flex-wrap items-center gap-2">
                                                <Button
                                                  size="sm"
                                                  onClick={() => repairFlexibleShippingMethod(zone, method, draft)}
                                                  loading={busyKey === `method:repair:${zone.id}:${method.instance_id}`}
                                                >
                                                  Utwórz regułę z podanej stawki
                                                </Button>
                                                {externalSettingsUrl && (
                                                  <a
                                                    href={externalSettingsUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1.5 font-medium text-danger hover:underline"
                                                  >
                                                    Otwórz ustawienia zaawansowane
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                  </a>
                                                )}
                                              </div>
                                            </div>
                                          </Alert>
                                        )}

                                        {isExternalMethod && !invalidFlexibleShippingRules && externalSettingsUrl && (
                                          <div className="rounded-card border border-border-subtle bg-surface-base/40 px-4 py-3 text-xs text-text-muted">
                                            Warunki dostępności tej metody oblicza zewnętrzna wtyczka.{' '}
                                            <a
                                              href={externalSettingsUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="inline-flex items-center gap-1 font-medium text-accent-primary hover:underline"
                                            >
                                              Otwórz jej ustawienia
                                              <ExternalLink className="h-3 w-3" />
                                            </a>
                                          </div>
                                        )}

                                          <div className="grid gap-3 md:grid-cols-2">
                                          <label className="text-sm text-text-secondary">Czas wysyłki (nadanie)
                                            <Input value={draft.dispatchEstimate ?? ''} maxLength={160} placeholder="Np. w ciągu 24 godzin" onChange={(e) => setMethodDrafts(prev => ({ ...prev, [key]: { ...draft, dispatchEstimate: e.target.value } }))} />
                                          </label>
                                          <label className="text-sm text-text-secondary">Sposób odbioru
                                            <Select value={draft.deliveryKind ?? 'address'} onChange={(e) => setMethodDrafts(prev => ({ ...prev, [key]: { ...draft, deliveryKind: e.target.value as 'address' | 'pickup' } }))}>
                                              <option value="address">Dostawa pod adres</option><option value="pickup">Odbiór w punkcie</option>
                                            </Select>
                                          </label>
                                        </div>
                                        {draft.deliveryKind === 'pickup' && <div className="grid gap-3 md:grid-cols-3">
                                          <label className="text-sm text-text-secondary">Mapa punktów
                                            <Select value={draft.pickupProvider ?? 'plugin'} onChange={e => setMethodDrafts(prev => ({ ...prev, [key]: { ...draft, pickupProvider: e.target.value as 'plugin' | 'inpost-geowidget' } }))}>
                                              <option value="plugin">Dotychczasowa wtyczka</option><option value="inpost-geowidget" disabled={!hasPickupMap}>InPost Geowidget</option>
                                            </Select>
                                          </label>
                                          {!hasPickupMap && <p className="md:col-span-3 text-xs text-text-muted">{pickupMap.isError ? 'Nie można sprawdzić konfiguracji mapy. Dostęp wymaga administratora.' : 'Brak aktywnej konfiguracji mapy dla tego sklepu lub wszystkich sklepów.'} <Link to="/integrations/pickup-maps" className="text-accent-primary hover:underline">Konfiguracja map</Link>. Do czasu jej udostępnienia działa mapa wtyczki.</p>}
                                          {draft.pickupProvider === 'inpost-geowidget' && <>
                                            <label className="text-sm text-text-secondary">Rodzaj punktów
                                              <Select value={draft.pickupPointTypes ?? 'all'} onChange={e => setMethodDrafts(prev => ({ ...prev, [key]: { ...draft, pickupPointTypes: e.target.value as 'all' | 'lockers' | 'points' } }))}>
                                                <option value="all">Automaty i punkty</option><option value="lockers">Automaty</option><option value="points">Punkty</option>
                                              </Select>
                                            </label>
                                            <label className="text-sm text-text-secondary">Zapis dla wysyłki
                                              <Select value={draft.pickupStorage ?? 'native'} onChange={e => setMethodDrafts(prev => ({ ...prev, [key]: { ...draft, pickupStorage: e.target.value as 'native' | 'furgonetka' } }))}>
                                                <option value="native">EcomFlow (metody WooCommerce)</option><option value="furgonetka">Furgonetka 1.9.9</option>
                                              </Select>
                                            </label>
                                            <p className="md:col-span-3 text-xs text-text-muted">Wymaga aktywnej integracji Geowidget dla tego sklepu i trybu Mapy EcomFlow. Most Furgonetki obsługuje pojedynczą paczkę InPost. Pozostałe integracje zachowują swoją mapę.</p>
                                            {(draft.pickupStorage ?? 'native') === 'native' && !CORE_SHIPPING_METHOD_IDS.has(method.method_id) && <p className="md:col-span-3 text-xs text-warning">Ta metoda pochodzi z zewnętrznej wtyczki i nie obsługuje zapisu natywnego. Zachowa własną mapę. Jeżeli jest przypisana do InPost w Furgonetce 1.9.9, wybierz zapis Furgonetka.</p>}
                                          </>}
                                        </div>}
                                        {showCostField && (
                                        <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                                            <FormField
                                              label="Koszt dostawy netto"
                                              htmlFor={`shipping-method-cost-${key}`}
                                              helper={isExternalMethod
                                                ? 'Kwota netto używana w koszyku. Jeżeli reguły wtyczki są uszkodzone, wpisz kwotę i użyj przycisku naprawy powyżej.'
                                                : 'Kwota netto stosowana dla tej metody w koszyku.'}
                                            >
                                              <Input
                                                id={`shipping-method-cost-${key}`}
                                                inputMode="decimal"
                                                value={methodCostInputValue(method, draft)}
                                                placeholder="0"
                                                onChange={(event) => updateMethodCost(method, draft, key, event.target.value)}
                                              />
                                            </FormField>
                                          </div>
                                        )}

                                        <div className="rounded-card border border-border-subtle bg-surface-base/40 p-4">
                                          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                              <div className="flex h-14 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-surface-raised">
                                                {draft.logoUrl ? (
                                                  <img src={draft.logoUrl} alt="" className="max-h-full max-w-full object-contain" />
                                                ) : (
                                                  <ImageIcon className="h-5 w-5 text-text-muted" />
                                                )}
                                              </div>
                                              <div className="min-w-0">
                                                <div className="text-sm font-medium text-text-primary">Logo na karcie dostawy</div>
                                                <div className="mt-0.5 text-xs text-text-muted">Widoczne w koszyku przy tej metodzie dostawy.</div>
                                              </div>
                                            </div>
                                            <Button
                                              variant="secondary"
                                              size="sm"
                                              leftIcon={<ImageIcon />}
                                              onClick={() => setMethodLogoPicker({ zoneId: zone.id, instanceId: method.instance_id })}
                                            >
                                              Wybierz logo
                                            </Button>
                                          </div>
                                          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_180px_auto] lg:items-end">
                                            <Input
                                              value={draft.logoUrl}
                                              placeholder="https://..."
                                              onChange={(event) => setMethodDrafts(prev => ({
                                                ...prev,
                                                [key]: { ...draft, logoUrl: event.target.value },
                                              }))}
                                            />
                                            <FormField
                                              label="Maks. szerokość"
                                              htmlFor={`shipping-logo-width-${key}`}
                                              helper="W pikselach"
                                            >
                                              <Input
                                                id={`shipping-logo-width-${key}`}
                                                type="number"
                                                min={SHIPPING_LOGO_WIDTH_MIN}
                                                max={SHIPPING_LOGO_WIDTH_MAX}
                                                value={draft.logoMaxWidth}
                                                onChange={(event) => setMethodDrafts(prev => ({
                                                  ...prev,
                                                  [key]: { ...draft, logoMaxWidth: event.target.value },
                                                }))}
                                              />
                                            </FormField>
                                            <Button
                                              variant="icon"
                                              leftIcon={<X />}
                                              disabled={!draft.logoUrl}
                                              onClick={() => setMethodDrafts(prev => ({
                                                ...prev,
                                                [key]: { ...draft, logoUrl: '' },
                                              }))}
                                              aria-label="Usuń logo metody dostawy"
                                            />
                                          </div>
                                        </div>

                                        {cleanSettings.length > 0 && (
                                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                            {cleanSettings.map(([settingKey, setting, config]) => renderSettingInput({
                                              settingKey,
                                              setting,
                                              config,
                                              value: draft.settings[settingKey] ?? '',
                                              onChange: (value) => setMethodDrafts(prev => ({
                                                ...prev,
                                                [key]: {
                                                  ...draft,
                                                  settings: { ...draft.settings, [settingKey]: value },
                                                },
                                              })),
                                            }))}
                                          </div>
                                        )}

                                        <div className="rounded-card border border-danger/30 bg-danger/[0.04] p-4">
                                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                              <div className="text-sm font-semibold text-danger">Usuń metodę dostawy</div>
                                              <p className="mt-1 text-xs text-text-muted">
                                                Metoda zostanie trwale usunięta z tej strefy w WooCommerce.
                                              </p>
                                            </div>
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              leftIcon={<Trash2 />}
                                              onClick={() => void deleteMethod(zone, method)}
                                              loading={busyKey === `method:delete:${zone.id}:${method.instance_id}`}
                                            >
                                              Usuń metodę
                                            </Button>
                                          </div>
                                        </div>
                                      </SortableShippingMethod>
                                    );
                                  })}
                                </SortableContext>
                              </DndContext>
                            )}
                          </div>
                            </div>
                          </SortableShippingZone>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </>
            )}
          </div>
          </SettingsSection>

          <SettingsSaveBar
            saving={savingShipping}
            status={saveStatus}
            onSave={saveShipping}
            label="Zapisz sklep"
            successLabel="Zapisano wysyłkę"
            errorLabel="Błąd zapisu wysyłki"
          />
            </>
          )}

        </>
      )}

      {adapter.isTenantScoped && siteIconPickerOpen && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onClose={() => setSiteIconPickerOpen(false)}
          onSelect={(url, item) => {
            setWpSiteForm(prev => ({
              ...prev,
              siteIconUrl: url,
              siteIconId: item?.id ?? prev.siteIconId,
            }));
            setSiteIconPickerOpen(false);
          }}
        />
      )}

      {adapter.isTenantScoped && methodLogoPicker && (
        <WpMediaPickerModal
          integrationId={integrationId}
          onClose={() => setMethodLogoPicker(null)}
          onSelect={(url) => selectMethodLogoFromMedia(url)}
        />
      )}
    </div>
  );
}
